import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, getToken, handleSessionExpired } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import { OnboardingBanner } from '@/components/OnboardingBanner';
import { SidebarNav } from '@/chat/SidebarNav';
import { ChatSidebarPanel, type ChatScope, type ChatSessionSummary } from '@/chat/ChatSidebarPanel';
import { SettingsModal } from '@/chat/SettingsModal';
import { ConnectorsPanel } from '@/chat/ConnectorsPanel';
import { WorkflowsPanel } from '@/chat/WorkflowsPanel';
import { ExtensionsPanel } from '@/chat/ExtensionsPanel';
import { FilesPanel } from '@/chat/FilesPanel';
import { PreviewPane, type PreviewTarget } from '@/chat/PreviewPane';
import { MissionStudio, MissionStudioSkeleton } from '@/chat/MissionStudio';
import { EmptyState } from '@/chat/EmptyState';
import { pickGreeting } from '@/chat/greeting';
import { couldRenderInline } from '@/chat/figure-intent';
import type { StarterSuggestion } from '@/chat/EmptyState';
import { Composer } from '@/chat/Composer';
import { SolutionBar, type SolutionDef } from '@/chat/SolutionBar';
import { Messages, type Msg, type AgenticFile, type AttachedFile, type MissionPlan, type MissionSource, type MissionImage } from '@/chat/Messages';
import { speakText, stopSpeaking, VOICE_MODE_INSTRUCTIONS } from '@/chat/voice';
import { VoiceMode } from '@/chat/VoiceMode';
import type { ToolResultSidecar } from '@/chat/remote-cards';
import type { Usage } from '@/chat/billing/types';

/** Starter prompts on the fresh-chat hero — lead-ins the user finishes, then sends.
 *  `icon` is an explicit lucide glyph name (see pickerIcons GLYPHS), rendered as a
 *  native line-icon — not an emoji — so a future relabel can't silently swap it. */
const STARTER_SUGGESTIONS: StarterSuggestion[] = [
  { icon: 'mail', label: 'Draft an email', prompt: 'Help me draft a professional email about ' },
  { icon: 'file-text', label: 'Summarize', prompt: 'Summarize the key points of ' },
  { icon: 'sparkles', label: 'Brainstorm', prompt: 'Brainstorm 10 creative ideas for ' },
  { icon: 'lightbulb', label: 'Explain', prompt: 'Explain in simple terms: ' },
];

/** Should we show the "forming your mission…" skeleton for this turn? ONLY when the
 *  PROMPT carries clear actionable/comparative/build intent — that is the only signal
 *  that reliably predicts what the server planner will decide.
 *
 *  This deliberately does NOT trigger on "a long, list-structured answer" any more.
 *  That signal fired on essentially every substantive reply (most good answers are
 *  numbered/bulleted), while the planner correctly classifies a factual explainer as
 *  missionType:'answer' — which is not showable. The skeleton therefore mounted and
 *  then unmounted with nothing behind it, so the Agent Studio appeared to "open and
 *  instantly close on its own" on ordinary Q&A. A real mission this misses still
 *  opens the studio the moment its plan lands — it just forgoes the head-start
 *  skeleton. Rule: never flash a panel we might not be able to keep. */
const MISSION_INTENT_RE = /\b(compare|comparison|versus|vs\b|top\s+\d+|best\s+\d+|research|analy[sz]e|analysis|build\s+(me\s+)?a|create\s+(me\s+)?a|make\s+(me\s+)?a|design\s+(me\s+)?a|plan\s+(a|my|for|an)|strateg(y|ies)|road\s?map|dashboard|report|deck|presentation|spreadsheet|generate\s+a|recommend|which\s+.+\b(should|is\s+best)|how\s+(do|can|should)\s+i\b|help\s+me\s+(build|create|plan|make|choose|decide|design|find)|step[-\s]by[-\s]step|guide\s+(to|for)|options\s+for|pros\s+and\s+cons|under\s+\$?\d|for\s+(a|my)\s+(family|team|business|startup|beginner|trip|wedding|event|home))\b/i;
function looksLikeMission(prompt: string): boolean {
  return MISSION_INTENT_RE.test(prompt || '');
}

/** Is this plan worth showing the studio for? THE single source of truth — the
 *  effect (which pins the mission) and the render (which draws it) must never
 *  drift apart, or the studio pins to a turn the render then refuses to draw.
 *
 *  `committed` = we already promised this turn a panel (its prompt carried clear
 *  mission intent, so the skeleton is on screen). In that case ANY plan with real
 *  work in it keeps the panel, even if the planner labelled it 'answer':
 *  `missionType` is a fallible LLM label — off-enum values like "explain" are
 *  silently coerced to 'answer' server-side (gateway-server parseMissionPlan), and
 *  live repros show full plans (agents + deliverables + actions) discarded purely
 *  over that string. The CONTENT is the product. Never break a promise over a label. */
function missionShowable(plan: MissionPlan | null | undefined, committed = false): boolean {
  if (!plan) return false;
  // Enriching live — the studio mounts now and streams sources in.
  if (plan.live?.provider === 'pending') return true;
  // Sources arrive later via /api/mission/gather, so this is false on the initial
  // plan but true once gather merges them in (the render re-evaluates).
  if (Array.isArray(plan.sources) && plan.sources.length > 0) return true;
  const hasWork = plan.agents.length > 0 || plan.deliverables.length > 0 || plan.actions.length > 0;
  if (!hasWork) return false;
  return committed || plan.missionType !== 'answer';
}

interface SessionListResponse { sessions: ChatSessionSummary[]; hasMore?: boolean }
interface SessionCreateResponse { id: string }
interface FullSessionResponse {
  id: string;
  title: string;
  /** True when older messages exist beyond the returned page (scroll-up loads them). */
  hasMoreOlder?: boolean;
  messages: Array<{
    id: string;
    role: 'user' | 'assistant' | 'system' | 'tool';
    content: string;
    wrapper?: string | null;
    metadata?: Record<string, unknown> | null;
    ts?: number;
  }>;
}

/** Pull persisted tool-result sidecars off a message row's metadata so reloaded
 *  card-bearing turns rehydrate as cards (not just the markdown fallback). */
function toolResultsFromMeta(meta: Record<string, unknown> | null | undefined): ToolResultSidecar[] | undefined {
  const raw = (meta as { toolResults?: unknown } | null | undefined)?.toolResults;
  return Array.isArray(raw) && raw.length > 0 ? (raw as ToolResultSidecar[]) : undefined;
}

// Deterministic fast-path for agent routing. Mirrors the server heuristic in
// intent-classify.ts so an unambiguous "create a pdf / make an excel / generate
// an image" routes straight to the agent WITHOUT depending on the classifier
// round-trip (which may be slow, mis-fire on noisy prompts, or 404 before a
// server restart). Stem-based verbs tolerate typos ("createa", "makea"). High
// precision — the SLM classifier is the fallback for phrasings this misses.
function looksLikeAgentTask(prompt: string): 'create_file' | 'generate_image' | null {
  const p = ` ${prompt.toLowerCase().slice(0, 600)} `;
  const BUILD = '(creat\\w*|mak\\w*|generat\\w*|buil\\w*|export\\w*|sav\\w*|writ\\w*|draft\\w*|produc\\w*|prepar\\w*|convert\\w*|give me|put\\b[^.?!\\n]{0,30}\\b(?:in|into))';
  // Extensions are listed bare (md, txt, html), never dotted (\.md): the gap
  // between BUILD and FILE below is [^.?!\n], which excludes dots so a match
  // can't run across a sentence break. The `\.?` lets the dot of ".md" sit flush.
  // UI/design deliverables (landing page, dashboard, ui, web app, …) are in this
  // fast-path too, kept in sync with the server bridge's design route (SKILL_HINTS
  // in hermes-bridge.ts). They used to rely on the SLM classifier fallback, but
  // that mis-fires or (as seen in prod) fails on a flaky upstream, sending
  // "make a landing page" to plain chat — which interrogates and outlines instead
  // of building. High-precision web terms only; mockup/logo stay with image-gen.
  const FILE = '(pdf|word\\s*doc\\w*|docx?|excel|xlsx?|spread\\s*sheet|csv|power\\s*point|pptx?|slide\\s*deck|slides?|presentation|markdown|md|readme|notes|html?|web\\s*page|web\\s*app|webapp|landing\\s*page|dashboard|website|\\bui\\b|user\\s*interface|front[\\s-]?end|frontend|prototype|txt|\\bfile\\b|\\bspreadsheet\\b)';
  if (new RegExp(`\\b${BUILD}\\b[^.?!\\n]{0,60}\\.?\\b${FILE}\\b`, 'i').test(p)) return 'create_file';

  // ── Image generation (mirrors the server looksLikeImageGenRequest) ──────────
  // Plural-tolerant noun; generation verbs, deliver/request verbs, and the strong
  // "<noun> of ___" cue. SUPPRESS analysis of an existing image so "describe this
  // photo" falls through to the classifier (usually an upload → agent) not gen.
  const IMG = '(image|img|imgs|picture|pic|pics|photo|photograph|logo|poster|illustration|drawing|painting|icon|banner|wallpaper|artwork|graphic|visual|thumbnail|sketch|portrait|avatar|mockup|mock-up|meme|sticker|emoji|gif|imge|iamge|imag|imange|imaeg|pictuer|picure|photograpgh)s?';
  const IMG_GEN = `(${BUILD}|draw\\w*|sketch\\w*|design\\w*|illustrat\\w*|paint\\w*|render\\w*|visuali[sz]\\w*|imagin\\w*|recreat\\w*|compos\\w*|craft\\w*)`;
  const IMG_REQ = '(giv\\w*|show\\w*|want\\w*|need\\w*|\\bget\\b|gimme|wanna|provid\\w*|send\\w*|share\\w*|display\\w*|fetch\\w*|lemme see|let me see)';
  const IMG_ANALYZE = '(what|which|describ\\w*|explain\\w*|analy[sz]\\w*|\\bread\\b|extract\\w*|caption\\w*|identif\\w*|recogni[sz]\\w*|ocr|is this|is that|whats|tell me about)';
  const REF = '(this|that|these|those|the|my|your|uploaded|attached|above|below|it|here)';
  const IMG_STRONG = '(draw|sketch|paint|illustrate|doodle)';
  const IMG_NONVIS = '(conclusion|conclusions|comparison|comparisons|parallel|parallels|distinction|distinctions|attention|blank|breath)';
  if (prompt.trim().toLowerCase().startsWith('/imagine')) return 'generate_image';
  if (new RegExp(`\\b${IMG_ANALYZE}\\b[^.?!\\n]{0,40}\\b${REF}\\s+${IMG}\\b`, 'i').test(p)) return null;
  if (new RegExp(`\\b${IMG}\\s+of\\b`, 'i').test(p)) return 'generate_image';
  if (new RegExp(`\\b${IMG_REQ}\\b[^.?!\\n]{0,30}\\b${IMG}\\b`, 'i').test(p)) return 'generate_image';
  if (new RegExp(`\\b${IMG_GEN}\\b[^.?!\\n]{0,60}\\b${IMG}\\b`, 'i').test(p)
      || new RegExp(`\\b${IMG}\\b[^.?!\\n]{0,40}\\b${IMG_GEN}\\b`, 'i').test(p)) return 'generate_image';
  if (new RegExp(`\\b${IMG_STRONG}\\w*\\s+(a|an|the|me|some|my|us|one|two|three|four|five|\\d+)\\b`, 'i').test(p)
      && !new RegExp(`\\b${IMG_NONVIS}\\b`, 'i').test(p)) return 'generate_image';
  return null;
}

/** Cheap NEGATIVE guard for the /api/agentic-intent round-trip.
 *
 *  That classifier is `await`ed BEFORE the chat call, so its full latency lands on
 *  every message — measured at 1.0-1.8s and ~2,400 input tokens, even to decide
 *  that "hi" is not a file task. `looksLikeAgentTask` above is the high-precision
 *  POSITIVE fast-path; this is the equally-cheap NEGATIVE one: when a prompt
 *  contains no verb that could produce/fetch anything and names no artifact, the
 *  agent is not a candidate and the LLM has nothing to decide.
 *
 *  Skipping is SAFE by the caller's own design: when this classifier errors or
 *  times out the caller already falls through to plain chat, which is exactly
 *  where a skipped prompt goes. So a miss costs a chat turn, never a wrong route.
 *  Deliberately conservative — anything long, or carrying any action/artifact
 *  word, still pays for the classifier. */
function clearlyNotAgentTask(prompt: string): boolean {
  const t = prompt.trim().toLowerCase();
  if (!t || t.length > 120) return false;   // long/complex → let the classifier judge
  // Any verb implying "produce something" or "go get something".
  if (/\b(creat|mak|generat|buil|export|sav|writ|draft|produc|prepar|convert|draw|sketch|design|illustrat|paint|render|visuali|download|upload|attach|send|email|schedul|book|updat|delet|add|remov|fetch|search|find|look\s*up|list|analy|summari|extract|read|open|run|execut|calculat|plot|chart|graph|translat|compar)\w*/.test(t)) return false;
  // Any artifact noun — even without a verb ("a pdf of last month?").
  if (/\b(pdf|docx?|xlsx?|csv|pptx?|slides?|presentation|spread\s*sheet|file|image|picture|photo|logo|report|deck|document|invoice|dashboard|excel|word)\b/.test(t)) return false;
  return true;
}

/** Mirror of the server's looksLikeImageRefinement (intelligence/image/refine.ts):
 *  an edit directive on a just-generated image ("make it bluer", "add a sunset",
 *  "zoom out"). Used to pin an image-refine follow-up to /api/chat — the Hermes
 *  agent has NO image tool, so the agentic-intent classifier misreading "make it
 *  blue" as agentic makes it write a text file instead of redrawing. Only consulted
 *  when a recent generated image exists, so it can be generous; tuned to avoid
 *  false positives (questions, "…to my calendar"/task topic-switches). */
function looksLikeImageRefinement(prompt: string): boolean {
  const t = prompt.toLowerCase().trim();
  if (!t || t.length > 200) return false;
  if (t.endsWith('?')) return false;
  if (/^(what|why|how|who|whom|whose|where|when|which|is|are|was|were|do|does|did|can|could|would|should|will|tell me|describe|explain)\b/.test(t)) return false;
  // Topic-switch veto: a personal destination or a task noun isn't an image edit.
  if (/\b(to|in|on|into|from)\s+(my|our)\b/.test(t)) return false;
  if (/\b(calendar|e-?mail|meeting|remind(er)?|to-?do|todo|task|list|note|event|message|contact|file|folder|document|doc|spreadsheet|invoice|cart|order|booking|appointment|password|account|profile|setting)s?\b/.test(t)) return false;
  // Unambiguous visual op → always an image edit.
  if (/\b(zoom|crop|rotate|flip|re-?colou?r|repaint|re-?render|render|re-?draw|redraw|recreate|regenerate|re-?generate|brighten|darken|sharpen|blur|upscale|enlarge|shrink|mirror)\b/.test(t)) return true;
  const editVerb = /\b(make|change|turn|add|remove|delete|drop|replace|swap|put|give|set|edit|adjust|modify|update|paint)\b/;
  const anaphora = /\b(it|its|it's|that|this|the (image|picture|photo|pic|drawing|art|one|sky|background|foreground|lighting|colou?rs?))\b/;
  if (editVerb.test(t) && anaphora.test(t)) return true;
  if (editVerb.test(t) && /\b\w{3,}(er|ier)\b/.test(t)) return true;
  if (/^(add|remove|delete|drop|replace|put|insert)\b/.test(t)) return true;
  if (/^(with |without |now |also |plus |same but |but )/.test(t)) return true;
  if (/^(more|less|bigger|smaller|brighter|darker|lighter|warmer|cooler|bluer|redder|greener|sharper|softer|closer)\b/.test(t)) return true;
  if (/^\w{3,}(er|ier)$/.test(t)) return true;
  return false;
}

/** A QUESTION / analysis about a just-generated image ("what is this?", "what
 *  colour is it?", "describe it"). Consulted only when a recent generated image
 *  exists, to route the follow-up to a TEXT answer (with grounding) instead of
 *  regenerating — critical under the Brew Image wrapper, which would otherwise try
 *  to generate an image OF the question text ("what is this" → a refusal). */
function looksLikeImageQuestion(prompt: string): boolean {
  const t = prompt.toLowerCase().trim();
  if (!t) return false;
  if (t.endsWith('?')) return true;
  return /^(what|which|why|how|who|whom|whose|where|when|is|are|was|were|does|do|did|can|could|would|will|tell me|describe|explain|analy[sz]e|identify|caption|what'?s|whats|does it|is it|is this)\b/.test(t);
}

/** Did the assistant recently generate an image in this conversation? (image
 *  wrapper or the `![Generated image](…)` reply markdown, over the recent window.) */
function hasRecentGeneratedImage(history: Msg[]): boolean {
  return history.slice(-6).some(
    (m) => m.role === 'assistant' && (m.wrapper === 'brew-imagen' || /!\[Generated image\]\(/.test(m.content)),
  );
}

/** Did the assistant recently generate a real DOCUMENT/file (not an image) in an
 *  agentic turn? Detected from the `files` array the gateway records after a
 *  create-file turn (a .txt/.pdf/.docx/.xlsx/… written to the user's persistent
 *  workspace). Used to route a follow-up QUESTION about it to /api/chat, where
 *  the gateway digest grounds the answer on the actual file contents. */
const IMG_FILE_EXT_RE = /\.(png|jpe?g|gif|webp|svg|bmp|avif|heic)$/i;
function hasRecentGeneratedFile(history: Msg[]): boolean {
  return history.slice(-6).some(
    (m) => m.role === 'assistant' && Array.isArray(m.files)
      && m.files.some((f) => f && typeof f.name === 'string' && !IMG_FILE_EXT_RE.test(f.name)),
  );
}

// NOTE: the former looksLikeDocumentQuestion / looksLikeDeliverableFollowUp
// phrasing heuristics were removed with the move to STICKY WORKSPACE routing
// (see the send handler): once a conversation has a recent produced file, its
// follow-ups go to the agent regardless of phrasing, because the agent shares
// the same persistent workspace and can both answer about and edit the file.
// Phrasing no longer decides that — file presence does.

/** Retry a persistence write (session-create / message-append) across TRANSIENT
 *  failures — the remote DB occasionally blips (DNS/connection reset), and without
 *  a retry the write is silently lost and the whole chat disappears on switch
 *  (a real reported bug). A few quick attempts with exponential backoff; re-throws
 *  the last error so the caller can still surface a "couldn't save" state. */
async function withRetry<T>(fn: () => Promise<T>, attempts = 4): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < attempts; i++) {
    try { return await fn(); }
    catch (e) {
      lastErr = e;
      if (i < attempts - 1) await new Promise((r) => setTimeout(r, Math.min(4000, 400 * 2 ** i)));
    }
  }
  throw lastErr;
}

/** A concept/educational DIAGRAM request ("create a diagram of X", "flowchart of
 *  the login process", "sequence diagram for checkout"). These are pinned to the
 *  normal chat path — the chat model renders an inline mermaid diagram (Breww
 *  draws it in-chat) — instead of the Hermes agent, which either dropped an
 *  unnecessary .excalidraw/.html file (the reported bug) or just described it in
 *  prose. Excludes explicit file/download/excalidraw/architecture asks: those are
 *  genuine file deliverables and stay on the agentic path. */
// Delegates to the shared vocabulary in chat/figure-intent.ts (mirrored from
// intelligence/figure-intent.ts, enforced by a parity test) — this function used
// to carry its own copy of the noun list, one of four that drifted apart.
//
// The lane it pins to now renders BOTH inline formats, ```mermaid and ```svg, so
// pinning a geometry ask here no longer means "a flowchart or nothing" — which
// was the routing half of the reported right-angled-triangle bug.
const looksLikeConceptDiagram = couldRenderInline;

/** The two agents the gateway can route a turn to (POST /api/send `agent`).
 *  'hermes' = Assistant (the shared always-on gateway); 'brewcode' = Builder
 *  (spawns `brewcode serve` in the user's own workspace and writes real files). */
export type AgentId = 'hermes' | 'brewcode';

/** Lifecycle of an agent's bridge, from the gateway's `engine_status` events.
 *  'ready' mirrors the bridge's REAL ready — the engine booted — not merely
 *  that we asked it to start, so the badge can't claim a dead spawn is live. */
export type EngineState = 'warming' | 'ready' | 'stopped';

/** The wrapper that means "use the Builder". Picking it in the wrapper menu
 *  routes the turn to the brewcode agent instead of the assistant — the same
 *  way brew-imagen means "generate an image". The entry already existed and
 *  already said 'Brew Code'; until now it only routed a CHAT turn to a coding
 *  model, so it said the right thing and did the wrong one. */
export const BUILDER_WRAPPER = 'brew-code';

// Default wrapper for a fresh chat — "Brew Native". Sent verbatim as `model`
// so the engine routes explicitly (no auto classifier) until the user picks a
// different wrapper from the "/" menu or the composer picker.
export const DEFAULT_WRAPPER = 'brew-generic-0.5';

/** Breww Pro mode taxonomy (Breww Pro brief §2): ONE default (Auto) + three
 *  overrides — replacing the confusing four-wrapper menu. Each maps to an
 *  underlying wrapper: Auto → the default (which sends 'auto', so the classifier
 *  routes); Fast → a small instant model; Pro → kimi + tools; Image → the image
 *  wrapper. Personas/products fold under Pro and stay reachable via the product
 *  switcher, so this menu stays a clean four. */
export type ProModeId = 'auto' | 'fast' | 'pro' | 'image';
export interface ProMode { id: ProModeId; wrapper: string; icon: string; label: string; desc: string; }
export const PRO_MODES: ProMode[] = [
  { id: 'auto',  wrapper: DEFAULT_WRAPPER, icon: '✨', label: 'Auto',  desc: 'Breww picks the best mode for you' },
  { id: 'fast',  wrapper: 'brew-fast',     icon: '⚡', label: 'Fast',  desc: 'Quick, everyday answers' },
  { id: 'pro',   wrapper: 'brew-pro',      icon: '✦', label: 'Pro',   desc: 'Researches, plans, and builds real files & tasks' },
  { id: 'image', wrapper: 'brew-imagen',   icon: '🎨', label: 'Image', desc: 'Make and edit images' },
];
/** The mode for a wrapper name — the four modes map directly; any other wrapper
 *  (a persona/product/solution) folds under Pro so the chip reads "Pro · <x>". */
export function modeForWrapper(name: string | null | undefined): ProMode {
  const hit = PRO_MODES.find((m) => m.wrapper === name);
  if (hit) return hit;
  return name === DEFAULT_WRAPPER || !name ? PRO_MODES[0]! : PRO_MODES[2]!;
}

/** A selectable wrapper, as returned by GET /api/wrappers. `name` is the value
 *  sent as `model` to FORCE that wrapper (bypassing the auto classifier). */
export interface WrapperDef {
  name: string;
  displayName: string;
  description?: string | null;
  icon?: string | null;
  connectorId?: string | null;
}

// Product/solution wrappers → their connector id (mirrors the server
// HARDCODED_ROUTING_PRODUCTS.connectorByWrapper). Used to split solutions out of
// the model dropdown AND to gate the chat Solution selector on connected status
// (linkedConnectors keys on connectorId). See docs/SOLUTION_SELECTION_PLAN.md.
const WRAPPER_TO_CONNECTOR: Record<string, string> = {
  'brew-esg-1.2': 'esg-caffe',
  'brew-lumara-1.0': 'lumara',
  'brew-dc-3.1': 'datacaffe',
  'brew-election-1.0': 'electioncaffe',
};
const SOLUTION_WRAPPER_NAMES = new Set(Object.keys(WRAPPER_TO_CONNECTOR));
/** A wrapper is a "solution" (product) — not a model persona — if it's a known
 *  product wrapper OR belongs to a non-core (product) connector. */
function isSolutionWrapper(w: WrapperDef): boolean {
  if (SOLUTION_WRAPPER_NAMES.has(w.name)) return true;
  const c = w.connectorId;
  return !!c && c !== 'brew-core';
}
/** The connector id for a solution wrapper (falls back to the name→connector map
 *  when the wrapper row's connectorId wasn't set by the local connector re-claim). */
function solutionConnectorId(w: WrapperDef): string {
  return w.connectorId || WRAPPER_TO_CONNECTOR[w.name] || w.name;
}

// The composer's model dropdown shows ONLY Breww's core model tiers — not the
// domain-persona wrappers (Health / Legal / BFSI / Design / Vision …), which
// share the base model, and not solutions (those live in the Solution selector /
// sidebar). Edit this list to change which models the user can pick between.
const MODEL_WRAPPER_NAMES = new Set([
  'brew-generic-0.5', // Basic — "Breww Native"
  'brew-pro',         // Pro — premium + web search
  'brew-imagen',      // Image — text-to-image
  'brew-reason',      // Reasoning — heavy multi-step
]);

// The product selected in the SHOW filter is also a routing hint: when set, the
// engine pins the turn to that connector (auth- + relevance-gated). 'all' → no hint.
const SCOPE_TO_CONNECTOR: Partial<Record<ChatScope, string>> = {
  esg: 'esg-caffe',
  lumara: 'lumara',
  datacaffe: 'datacaffe',
  electioncaffe: 'electioncaffe',
};
const CONNECTOR_TO_SCOPE: Record<string, ChatScope> = {
  'esg-caffe': 'esg',
  lumara: 'lumara',
  datacaffe: 'datacaffe',
  electioncaffe: 'electioncaffe',
};

/** Map the wrapper a turn routed to (brew-esg-1.2, brew-mcp-esg-caffe,
 *  brew-lumara-1.0, brew-dc-3.1, brew-election-1.0…) to a product scope, so a
 *  chat that auto-routes to a product gets tagged with it and shows under that
 *  product's dropdown. Non-product wrappers (brew-pro/generic) → null. */
function wrapperToScope(w: string | null | undefined): ChatScope | null {
  if (!w) return null;
  if (/esg/i.test(w)) return 'esg';
  if (/lumara/i.test(w)) return 'lumara';
  if (/datacaffe|\bdc-/i.test(w)) return 'datacaffe';
  if (/election/i.test(w)) return 'electioncaffe';
  return null;
}

// Product catalog — drives the topbar surface switcher AND the `/` command
// menu. `mfe: true` means the product ships a dedicated embedded UI (iframe at
// :8102, ESG only today); every other product runs inside the native chat with
// its connector pinned (connectorHint). `examples` are the use-case prompts the
// slash menu offers. Keyed/ordered here; filtered to what the user has linked.
export interface ProductDef {
  id: string;
  scope: ChatScope;
  name: string;
  icon: string;
  blurb: string;
  mfe?: boolean;
  examples: string[];
}
export const PRODUCTS: ProductDef[] = [
  {
    id: 'esg-caffe', scope: 'esg', name: 'ESG Caffe', icon: '🌱', mfe: true,
    blurb: 'BRSR/GRI reporting, emissions, filings',
    examples: [
      'Show me my company profile',
      'List my pending reviews',
      'Generate a BRSR PDF for the current financial year',
      "What's BRSR Core and which attributes need assurance?",
      'Explain GHG Scope 2 (location vs market-based)',
    ],
  },
  {
    id: 'lumara', scope: 'lumara', name: 'Lumara', icon: '💎',
    blurb: 'Sales, customers, orders',
    examples: ['Give me a sales summary', 'Show my top customers this month', 'List my pending orders'],
  },
  {
    id: 'datacaffe', scope: 'datacaffe', name: 'DataCaffe', icon: '📊',
    blurb: 'Datasets, charts, connections',
    examples: ['Chart my revenue by month', 'Summarize my latest dataset', 'Show my connection health'],
  },
  {
    id: 'electioncaffe', scope: 'electioncaffe', name: 'Election Caffe', icon: '🗳️',
    blurb: 'Turnout, booth results',
    examples: ['Show voter turnout trends', 'Summarize booth-level results'],
  },
];

const ESG_MFE_BASE_URL = (import.meta.env.VITE_ESG_MFE_URL || 'http://localhost:8102').replace(/\/+$/, '');
const ESG_MFE_LOAD_TIMEOUT_MS = Number(import.meta.env.VITE_ESG_MFE_LOAD_TIMEOUT_MS || 8000);
const ESG_MFE_READY_EVENT = 'brew:ready';
const ESG_MFE_ORIGIN = (() => {
  try {
    return new URL(ESG_MFE_BASE_URL).origin;
  } catch {
    return ESG_MFE_BASE_URL;
  }
})();

// When true (default), ESG renders its cards NATIVELY inside Brew's chat by
// consuming the engine's `tool_results` — no brew-mfe iframe. The native chat
// input routes to the esg-caffe connector (connectorHint), the product MCP/SLM
// returns card-ready tool_results, and Messages renders them inline. Set
// VITE_ESG_NATIVE_CARDS=false to fall back to the embedded iframe.
const ESG_NATIVE_CARDS = (import.meta.env.VITE_ESG_NATIVE_CARDS ?? 'true') !== 'false';

/**
 * /chat — full-viewport layout that mirrors the legacy SPA:
 *
 *   <Rail>  <Sidebar>  <Main>
 *
 * Rail (icon column) and Sidebar (chats list) use legacy CSS classes
 * via styles/legacy.css. Main is the chat area: top empty-state with
 * suggestion chips, or messages, then a sticky composer at the bottom.
 *
 * No top nav from <Layout> here — chat owns the entire viewport. Other
 * routes (/, /connectors, /brain, /admin) still use <Layout>.
 */
export function Chat() {
  const auth = useAuth();
  const navigate = useNavigate();

  // Settings modal lives at the shell level so the rail's gear (always visible)
  // can open it regardless of which sidebar section is mounted.
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'account' | 'billing' | 'usage'>('account');
  const openSettings = useCallback((tab: 'account' | 'billing' | 'usage') => {
    setSettingsTab(tab);
    setSettingsOpen(true);
  }, []);

  // Sidebar collapse — drives the SidebarNav between its expanded (icon+label
  // rows + section body) and collapsed (icon-only rail) states. Tracked in
  // state so the toggle + double-click share one source of truth.
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // Mobile drawer: below ~768px the sidebar leaves the flex flow and slides in
  // over a backdrop (desktop ignores this — see the @media block in styles.css).
  // Opening always forces the expanded (labels + section) look so the drawer is
  // usable regardless of the desktop collapse state.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const openDrawer = useCallback(() => { setSidebarCollapsed(false); setDrawerOpen(true); }, []);
  const closeDrawer = useCallback(() => setDrawerOpen(false), []);
  useEffect(() => {
    if (!drawerOpen) return;
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') setDrawerOpen(false); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);
  // Separate state: when set, swap the main pane to the embedded product MFE
  // (today only ESG — brew-mfe at :8102, same backend, product-branded UI).
  // Auto-set on mount if the signed-in Brew user has the connector linked, so
  // an ESG-authorized user lands on the ESG chat surface without an extra click.
  // Persisted across reloads: 'esg' = show the ESG MFE iframe, null = native
  // chat. localStorage('brewProductView') is 'esg' | 'native' | (absent). We
  // ONLY auto-default to ESG when there is NO stored choice (first visit) — so
  // once the user explicitly picks Native Brew it sticks across refreshes.
  const [productView, setProductView] = useState<'esg' | null>(
    // Native cards on → never start in the iframe view, even if a stale 'esg'
    // choice is stored from the iframe era. Off → respect the stored choice.
    () => (!ESG_NATIVE_CARDS && localStorage.getItem('brewProductView') === 'esg' ? 'esg' : null),
  );
  const [esgIframeStatus, setEsgIframeStatus] = useState<'idle' | 'loading' | 'ready'>('idle');
  // /auth/me payload — used to (a) gate the ESG toggle on the union of
  // Identity OR active Subscription, (b) show the linked product email in
  // the ESG iframe header. Identity can be reassigned to whichever user
  // most recently authorized the same product account, so checking only
  // identities would falsely hide the toggle for older subscribers.
  const [identities, setIdentities] = useState<Array<{ provider: string; email?: string }>>([]);
  const [subscriptions, setSubscriptions] = useState<Array<{ connectorId: string; status: string }>>([]);
  const linkedConnectors = new Set<string>([
    ...identities.map((i) => i.provider),
    ...subscriptions.filter((s) => s.status === 'active').map((s) => s.connectorId),
  ]);

  // On mount: fetch /api/auth/me to see which connectors the user has linked.
  // If `esg-caffe` is linked, default productView to 'esg' (the iframe to
  // brew-mfe). The user can still toggle back to the native Brew chat.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.get<{
          identities?: Array<{ provider: string; email?: string }>;
          subscriptions?: Array<{ connectorId: string; status: string }>;
        }>('/api/auth/me');
        if (cancelled) return;
        const ids = me.identities ?? [];
        const subs = me.subscriptions ?? [];
        setIdentities(ids);
        setSubscriptions(subs);
        const hasEsg = ids.some((i) => i.provider === 'esg-caffe')
          || subs.some((s) => s.connectorId === 'esg-caffe' && s.status === 'active');
        // First-visit default only: respect a stored choice (incl. an explicit
        // "Native Brew") so a refresh never yanks the user back into the iframe.
        if (hasEsg && localStorage.getItem('brewProductView') === null) {
          // Native cards (default): DO NOT pin to ESG. Leave the chat on
          // auto-route ('all', no connectorHint) so the engine routes each prompt
          // to whichever connected product can answer it — and asks (product-pick
          // card) only when 2+ products could. The product dropdown stays as an
          // optional manual override. Only the legacy iframe surface still pins.
          if (!ESG_NATIVE_CARDS) setProductView('esg');
        }
      } catch {
        /* no identities yet — stay on native Brew chat */
      }
    })();
    return () => { cancelled = true; };
    // Run once on mount; productView is intentionally not in deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the connected-solutions set fresh when the user links/unlinks a product
  // in the sidebar (ConnectorsPanel broadcasts on 'brew-connector-auth') or when
  // they return to the tab — so a newly-connected solution appears in the chat's
  // Solution selector without a full reload.
  useEffect(() => {
    const refetch = async (): Promise<void> => {
      try {
        const me = await api.get<{
          identities?: Array<{ provider: string; email?: string }>;
          subscriptions?: Array<{ connectorId: string; status: string }>;
        }>('/api/auth/me');
        setIdentities(me.identities ?? []);
        setSubscriptions(me.subscriptions ?? []);
      } catch { /* keep current */ }
    };
    let ch: BroadcastChannel | null = null;
    try {
      ch = new BroadcastChannel('brew-connector-auth');
      ch.onmessage = () => { void refetch(); };
    } catch { /* unsupported (Safari private) */ }
    const onFocus = (): void => { void refetch(); };
    window.addEventListener('focus', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      try { ch?.close(); } catch { /* noop */ }
    };
  }, []);

  // Build the iframe URL with auth context so brew-mfe skips its Connect gate.
  // brew-mfe reads `#esg_token=...` from the hash and persists it. The token
  // value doesn't authenticate downstream by itself — the real ESG identity
  // lives in Brew's Identity row server-side; this just unlocks the chat UI.
  // `esg_email` is the email of the LINKED ESG account (whichever the user
  // authorized in the connectors panel), not the brew login email — so the
  // iframe header shows "connected · <esg account>" not "connected · <brew>".
  const esgIframeUrl = (() => {
    const brewToken = getToken() ?? '';
    const esgIdentity = identities.find((i) => i.provider === 'esg-caffe');
    const email = esgIdentity?.email || auth.user?.email || '';
    const hash = new URLSearchParams({
      esg_token: brewToken || 'brew-linked',
      esg_email: email,
    });
    return `${ESG_MFE_BASE_URL}/#${hash.toString()}`;
  })();
  const [sessions, setSessions] = useState<ChatSessionSummary[]>([]);
  const [sidebarLoading, setSidebarLoading] = useState(true);
  // Server keyset pagination — the sidebar holds only the pages it has fetched.
  const [sessionsHasMore, setSessionsHasMore] = useState(false);
  const [sessionsLoadingMore, setSessionsLoadingMore] = useState(false);
  // Cursor = (updated_at_ms, id) of the last row we already have. In a ref (not
  // state) so loadMore reads the latest value without re-subscribing effects.
  const sessionsCursorRef = useRef<{ ts: number; id: string } | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  // Live mirror of activeId, readable inside async closures after an await (so an
  // in-flight older-page fetch can detect the user switched sessions meanwhile).
  const activeIdRef = useRef<string | null>(null);
  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);
  // Reopen the same chat after a page refresh. Capture the stored id ONCE at the
  // very first render — before the persist effect below can overwrite it with the
  // initial null activeId — then restore it on mount. Without this, refreshing
  // while inside a chat always dropped the user onto a blank "new chat".
  const restoreSessionRef = useRef<string | null>(localStorage.getItem('brewLastSessionId'));
  const didRestoreRef = useRef(false);
  const [messages, setMessages] = useState<Msg[]>([]);
  // ── Chat attachments (upload a doc/image, ask anything) ──────────────────
  // `pending` = uploaded but not yet sent (shown as removable chips in the
  // composer). `session` = the sticky set for THIS conversation — every agentic
  // turn re-sends the full list so follow-ups ("summarize it") work without
  // re-uploading. Rebuilt from message metadata on session load.
  const [pendingAttachments, setPendingAttachments] = useState<AttachedFile[]>([]);
  const [sessionAttachments, setSessionAttachments] = useState<AttachedFile[]>([]);
  const [uploading, setUploading] = useState(false);
  // ── Message pagination (reverse infinite scroll) ─────────────────────────
  // A reopened chat loads only its most-recent page; older turns load on scroll-up.
  const MESSAGES_PAGE = 40;
  const [msgHasMoreOlder, setMsgHasMoreOlder] = useState(false);
  const [msgLoadingOlder, setMsgLoadingOlder] = useState(false);
  // True while a chat's most-recent page is being fetched (loadSession). Drives a
  // message skeleton so a slow history read never looks like a frozen page (#4).
  const [msgLoading, setMsgLoading] = useState(false);
  // Synchronous in-flight guard — state updates async, so a burst of scroll events
  // near the top would otherwise fire several concurrent older-page fetches.
  const loadingOlderRef = useRef(false);
  // Cursor = (ts,id) of the OLDEST loaded message; the next page is strictly older.
  const oldestMsgCursorRef = useRef<{ ts: number; id: string } | null>(null);
  // "Am I pinned to the bottom?" — new/streamed turns only auto-scroll when true, so
  // reading older history isn't yanked down. Starts true (fresh chat sits at bottom).
  const stickToBottomRef = useRef(true);
  // Set just before a scroll-up prepend so a layout effect can restore the exact
  // read position (prepending taller content would otherwise jump the viewport).
  const pendingPrependRef = useRef<{ prevScrollHeight: number; prevScrollTop: number } | null>(null);
  // Force one scroll-to-bottom after a session (re)load, regardless of stick state.
  const forceBottomRef = useRef(false);
  // Is the reader scrolled meaningfully away from the live end? Mirrored in a ref
  // so the scroll handler can compare without re-rendering on every event.
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const awayFromBottomRef = useRef(false);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamingId, setStreamingId] = useState<string | null>(null);
  // ── Mission OS — the Agent Studio (Universal Agent Blueprint) ─────────────
  // After a substantive assistant turn finishes, ask /api/mission (fire-and-forget)
  // to PLAN the turn as a mission — a goal, dedicated agents, real deliverables,
  // and executable actions — and attach the plan to that message so the Agent
  // Studio can render it. A ref marks turns already handled so this never loops.
  const assistDoneRef = useRef<Set<string>>(new Set());
  // The mission the studio is currently PINNED to (its message id). Pinning (rather
  // than "newest assistant turn with a plan") keeps the studio stable while a
  // deliverable build turn streams, and stops a dismissed mission from reviving an
  // older one. A newer real mission repins; dismiss clears it. (review #4/#13)
  const [activeMissionId, setActiveMissionId] = useState<string | null>(null);
  // The turn currently being PLANNED (the /api/mission LLM call is in flight). Drives
  // an immediate "forming your mission…" skeleton so the studio never pops in cold
  // after a long silence — the user sees it working from the first moment.
  const [planningMissionId, setPlanningMissionId] = useState<string | null>(null);
  // Mobile: the studio is a slide-up sheet opened from a floating pill. Closed by
  // default (pill visible) and reset closed whenever the active mission changes.
  const [studioMobileOpen, setStudioMobileOpen] = useState(false);
  // Desktop: × HIDES the side panel (does NOT dismiss the mission) — a reopen pill
  // appears so the studio can always be brought back. Reset visible on a new mission.
  const [desktopStudioHidden, setDesktopStudioHidden] = useState(false);
  // Reset the open state only when the pinned MISSION changes — deliberately NOT on
  // planningMissionId. That id goes non-null→null the moment a plan lands, which used
  // to force-close a sheet the user had just opened from the "Forming mission…" pill
  // (on ≤1100px it slid up and straight back down, even for a perfectly good plan).
  useEffect(() => { setStudioMobileOpen(false); setDesktopStudioHidden(false); }, [activeMissionId]);
  // Close the studio (× / collapse / backdrop): hide it but keep it reopenable.
  const closeStudio = useCallback(() => { setStudioMobileOpen(false); setDesktopStudioHidden(true); }, []);
  const openStudio = useCallback(() => { setStudioMobileOpen(true); setDesktopStudioHidden(false); }, []);
  useEffect(() => {
    if (streaming) return; // wait until the turn is fully done
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant' || last.error) return;
    if (assistDoneRef.current.has(last.id)) return;
    assistDoneRef.current.add(last.id);
    const answer = typeof last.content === 'string' ? last.content : '';
    // Only substantive TEXT answers get a mission plan. Skip greetings, image
    // turns, and agentic file-builds / product cards (they get file cards + a
    // refine dock, and are already a delivered artifact).
    const skip =
      answer.trim().length < 80 || !!last.imageUrl ||
      (Array.isArray(last.files) && last.files.length > 0) ||
      (Array.isArray(last.toolResults) && last.toolResults.length > 0);
    if (skip) return;
    const priorUser = [...messages].reverse().find((m) => m.role === 'user');
    const prompt = priorUser && typeof priorUser.content === 'string' ? priorUser.content : '';
    if (!prompt) return;
    // Show the "forming your mission…" skeleton only on clear mission intent in the
    // prompt. A plain factual reply ("tell me about X", "give more detailed") must NOT
    // flash a skeleton that then vanishes when the planner returns answer-type.
    // `committed` — we promised this turn a panel, so the keep-rule below honours it.
    const committed = looksLikeMission(prompt);
    if (committed) setPlanningMissionId(last.id);
    void api.post<{ plan?: MissionPlan | null }>('/api/mission', { prompt, answer })
      .then((r) => {
        const plan = r?.plan ?? null;
        setMessages((ms) => ms.map((m) => (m.id === last.id ? { ...m, missionPlan: plan, missionQuery: prompt, missionCommitted: committed } : m)));
        // Pin the studio only if the plan is worth showing (shared predicate, so the
        // render can never disagree). NOTE: the server returns HTTP 200 {plan:null}
        // for its OWN failures (upstream down, unparseable JSON, 18s timeout), so a
        // null plan here is indistinguishable from a deliberate decline.
        if (missionShowable(plan, committed)) setActiveMissionId(last.id);
      })
      // Only a genuine transport failure lands here (the endpoint 200s its own
      // errors). Don't disturb chat, but don't swallow it silently either.
      .catch((e) => { console.warn('[mission] plan request failed', e); })
      .finally(() => setPlanningMissionId((id) => (id === last.id ? null : id)));
  }, [streaming, messages]);
  // Wrappers available for selection (fetched from /api/wrappers). The session
  // routes to `selectedWrapper`; it resets to DEFAULT_WRAPPER (Brew Native) for
  // a new chat and is pinned the moment the user picks a wrapper.
  const [wrappers, setWrappers] = useState<WrapperDef[]>([
    { name: DEFAULT_WRAPPER, displayName: 'Breww Generic', icon: '🔥', connectorId: null },
  ]);
  // Becomes true once the /api/wrappers fetch settles (ok or failed) — the
  // session restore waits for it so a reopened chat resolves its pinned wrapper.
  const [wrappersReady, setWrappersReady] = useState(false);
  const [selectedWrapper, setSelectedWrapper] = useState<string>(DEFAULT_WRAPPER);
  // The agent is DERIVED from the wrapper, not chosen separately: picking
  // 'Brew Code' in the wrapper menu IS picking the Builder. Same precedent as
  // brew-imagen, where the wrapper choice is the mode. A second switcher for
  // the same decision is the confusion we keep removing — and the wrapper is
  // already persisted per session, so this needs no state of its own.
  const agent: AgentId = selectedWrapper === BUILDER_WRAPPER ? 'brewcode' : 'hermes';
  // If the picked SOLUTION stops being connected (user disconnects it mid-
  // session), fall back to General. Otherwise `selectedWrapper` is stranded on a
  // solution the user just dropped: the UI shows General (it's no longer in the
  // connected set) but the next turn still sends model='brew-esg-1.2' and hits
  // the entitlement gate. Only reconciles SOLUTION picks — a persona/model
  // selection is left untouched. Keyed on the raw connect state (stable refs).
  useEffect(() => {
    if (selectedWrapper === DEFAULT_WRAPPER) return;
    const w = wrappers.find((x) => x.name === selectedWrapper);
    if (!w || !isSolutionWrapper(w)) return;
    const cid = solutionConnectorId(w);
    const stillConnected =
      identities.some((i) => i.provider === cid) ||
      subscriptions.some((s) => s.connectorId === cid && s.status === 'active');
    if (!stillConnected) setSelectedWrapper(DEFAULT_WRAPPER);
  }, [identities, subscriptions, wrappers, selectedWrapper]);
  // What the reply bubble says produced it. Both agents used to render
  // 'Breww · Agent', so a Builder reply and an Assistant reply were
  // indistinguishable — which is how a Hermes-written file got mistaken for
  // BrewCode's. Brew Code is a product name the user picked; it names itself.
  const agentLabel = agent === 'brewcode' ? 'Breww Code' : 'Agent';
  // Per-agent bridge lifecycle from the gateway's `engine_status`, replayed on
  // every stream open — so the badge reflects the live bridge, not a guess.
  const [engineState, setEngineState] = useState<Partial<Record<AgentId, EngineState>>>({});
  const [error, setError] = useState<string | null>(null);
  // Rolling 30-day token usage, polled for the in-chat usage reminder. null
  // until the first fetch settles (no banner shown while unknown).
  const [usage, setUsage] = useState<Usage | null>(null);
  // Token Saver: when on, the chat send asks the engine to use fewer tool calls
  // and skip the MCP composer LLM. Persisted so it sticks across reloads.
  const [tokenSaver, setTokenSaver] = useState<boolean>(
    () => localStorage.getItem('brewTokenSaver') === '1',
  );
  // Speak replies (Voice AI): when on, each finished assistant reply is spoken aloud
  // via OpenAI TTS (/api/tts). OFF by default — speech is a paid, explicit action
  // (cost guard, VOICE_AI_DESIGN §5). Combined with the mic, this is the hands-hear
  // half of a live voice conversation. Persisted across reloads.
  const [speakReplies, setSpeakReplies] = useState<boolean>(
    () => localStorage.getItem('brewSpeakReplies') === '1',
  );
  // Hands-free live voice conversation (VOICE_AI_DESIGN live mode). When open, the
  // VoiceMode overlay runs the mic→STT→send→stream→TTS→mic loop; the composer's
  // Speak-Replies effect is suppressed so the reply isn't spoken twice.
  const [voiceModeOpen, setVoiceModeOpen] = useState(false);
  // The last finalized assistant reply — what VoiceMode speaks. Id guards a re-speak.
  const lastAssistant = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m && m.role === 'assistant') return { id: m.id, content: m.content };
    }
    return null;
  }, [messages]);
  // Routing to the Hermes agentic engine (which runs tools + creates real files)
  // is now decided PER TURN by intent — the manual "Agent" toggle was removed.
  // We clear any stale persisted flag so an earlier "Agent on" can't stick the
  // whole app in agent mode (which made even "hi" route to the agent).
  useEffect(() => { localStorage.removeItem('brewAgentMode'); }, []);
  // The user dismissed the "turn on Token Saver" reminder for this session.
  const [saverReminderDismissed, setSaverReminderDismissed] = useState(false);
  const scrollerRef = useRef<HTMLDivElement>(null);
  // The file open in the Claude-style preview pane (right split view), or null
  // when the pane is closed. Opened from the Files panel / file chips.
  const [previewFile, setPreviewFile] = useState<PreviewTarget | null>(null);
  // Bumped when a turn finishes so the Files panel re-reads the workspace — a
  // file the agent just created then appears without a manual refresh.
  const [filesRefreshKey, setFilesRefreshKey] = useState(0);
  const prevStreamingRef = useRef(false);
  useEffect(() => {
    // Only on the streaming → idle EDGE (a turn just ended), not on every render.
    if (prevStreamingRef.current && !streaming) setFilesRefreshKey((k) => k + 1);
    prevStreamingRef.current = streaming;
  }, [streaming]);
  const abortRef = useRef<AbortController | null>(null);
  /** True while an AGENTIC turn is in flight on the Hermes bridge. Aborting the
   *  browser's fetch is enough for a plain chat turn, but the bridge is a
   *  separate long-lived process: it only learns a turn was cancelled if we send
   *  it an explicit `interrupt`. Without that it stays busy forever and refuses
   *  every later message with "Still working on your previous message". */
  const agentTurnRef = useRef(false);
  // The product scope this session is currently tagged with on the server
  // (metadata.product), so we only PATCH the tag when it actually changes.
  const taggedScopeRef = useRef<ChatScope | null>(null);
  const esgIframeRef = useRef<HTMLIFrameElement>(null);
  const esgIframeTimerRef = useRef<number | null>(null);

  /** True when the right-hand pane is showing the brew-mfe iframe instead of
   *  the native chat. Sidebar clicks and "new chat" need to be relayed into
   *  the iframe via postMessage in this case — otherwise they'd change brew
   *  web app state that's hidden behind the iframe. */
  const esgIframeActive = productView === 'esg' && linkedConnectors.has('esg-caffe');

  const clearEsgIframeTimer = useCallback(() => {
    if (esgIframeTimerRef.current !== null) {
      window.clearTimeout(esgIframeTimerRef.current);
      esgIframeTimerRef.current = null;
    }
  }, []);

  const fallbackToNativeEsgChat = useCallback((detail?: string) => {
    clearEsgIframeTimer();
    setEsgIframeStatus('idle');
    setProductView(null);
    setChatScope('esg');
    localStorage.setItem('brewProductView', 'native');
    setError(detail || `ESG embedded UI is unavailable right now. Switched to native Breww chat.`);
  }, [clearEsgIframeTimer]);

  useEffect(() => {
    if (!esgIframeActive) {
      clearEsgIframeTimer();
      setEsgIframeStatus('idle');
      return;
    }
    setError((prev) =>
      prev && /ESG embedded UI is unavailable/i.test(prev) ? null : prev,
    );
    setEsgIframeStatus('loading');
    clearEsgIframeTimer();
    esgIframeTimerRef.current = window.setTimeout(() => {
      fallbackToNativeEsgChat(
        `ESG embedded UI didn't load from ${ESG_MFE_BASE_URL}. Switched to native Breww chat.`,
      );
    }, ESG_MFE_LOAD_TIMEOUT_MS);
    return clearEsgIframeTimer;
  }, [clearEsgIframeTimer, esgIframeActive, fallbackToNativeEsgChat]);

  function postToEsgIframe(payload: { type: string; sessionId?: string }): void {
    const win = esgIframeRef.current?.contentWindow;
    if (!win) return;
    // origin '*' is safe here because we control the iframe src; the receiver
    // verifies sender origin against the brew web app origin allowlist.
    win.postMessage(payload, '*');
  }

  function handleSelectSession(id: string): void {
    if (esgIframeActive) {
      setActiveId(id);
      activeIdRef.current = id;
      postToEsgIframe({ type: 'brew:loadSession', sessionId: id });
    } else {
      void loadSession(id);
    }
  }

  function handleNewChat(): void {
    if (esgIframeActive) {
      newChat();
      postToEsgIframe({ type: 'brew:newChat' });
    } else {
      newChat();
    }
  }

  // Load the selectable wrapper list once on mount. Falls back to just Brew
  // Native if the endpoint is unavailable, so the picker always works.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get<{ data: WrapperDef[] }>('/api/wrappers');
        if (!cancelled && Array.isArray(r.data) && r.data.length > 0) setWrappers(r.data);
      } catch {
        /* keep the Brew Native fallback */
      } finally {
        if (!cancelled) setWrappersReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Pull the current token-usage state (count / limit) so the chat can show the
  // 50%-used reminder and the exhausted notice. Re-run after each turn (the
  // send() success path calls this) so the bar reflects what the turn spent.
  const refreshUsage = useCallback(async () => {
    try {
      const r = await api.get<{ usage: Usage }>('/api/v2/subscriptions/usage');
      if (r?.usage) setUsage(r.usage);
    } catch {
      /* usage is best-effort — never block chat on it */
    }
  }, []);
  useEffect(() => { void refreshUsage(); }, [refreshUsage]);

  // Persist the Token Saver choice so it survives reloads.
  useEffect(() => {
    localStorage.setItem('brewTokenSaver', tokenSaver ? '1' : '0');
  }, [tokenSaver]);

  // Persist Speak Replies across reloads.
  useEffect(() => {
    localStorage.setItem('brewSpeakReplies', speakReplies ? '1' : '0');
  }, [speakReplies]);

  // Speak the reply aloud once a turn FINISHES (Speak Replies on). Tracks the
  // last-spoken message id so a re-render never re-speaks; never speaks a still-
  // streaming or empty reply. Turning the toggle off stops any current utterance.
  // This is the "hear it back" half of a live voice conversation (mic → send →
  // spoken reply). Nothing speaks unless the user opted in — cost guard §5.
  const lastSpokenRef = useRef<string | null>(null);
  useEffect(() => {
    const last = messages[messages.length - 1];
    // While voice mode owns speech, mark the latest assistant message as already-spoken —
    // EVEN mid-stream — so closing the overlay during a reply never lets the composer's
    // Speak-Replies path re-speak it in the background.
    if (voiceModeOpen) {
      if (last && last.role === 'assistant') lastSpokenRef.current = last.id;
      return;
    }
    if (streaming) return;
    if (!last || last.role !== 'assistant' || !last.content || !last.content.trim()) return;
    if (!speakReplies) { stopSpeaking(); return; }
    if (lastSpokenRef.current === last.id) return;
    lastSpokenRef.current = last.id;
    void speakText(last.content, undefined, undefined, VOICE_MODE_INSTRUCTIONS).catch(() => {}); // A8: warm delivery, not flat
  }, [messages, streaming, speakReplies, voiceModeOpen]);


  // The embedded ESG brew-mfe posts:
  //   - { type: 'brew:ready' } once the real app is mounted inside the iframe
  //   - { type: 'brew:logout' } when the user hits its "Log out" button
  // We only trust messages from the configured ESG MFE origin.
  useEffect(() => {
    const onMsg = (e: MessageEvent) => {
      const data = e.data as { type?: string } | null;
      if (e.origin !== ESG_MFE_ORIGIN) return;
      if (data?.type === ESG_MFE_READY_EVENT) {
        if (!esgIframeActive) return;
        clearEsgIframeTimer();
        setError((prev) =>
          prev && /ESG embedded UI/i.test(prev) ? null : prev,
        );
        setEsgIframeStatus('ready');
        return;
      }
      if (data?.type === 'brew:logout') {
        void auth.logout().then(() => navigate('/login', { replace: true }));
      }
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [auth, clearEsgIframeTimer, esgIframeActive, navigate]);

  // Scroll orchestration (runs before paint, so no flicker):
  //  1. A scroll-up PREPEND restores the exact read position (keep the viewport on
  //     the same turn instead of jumping as taller older content is inserted above).
  //  2. Otherwise pin to the bottom only when a (re)load forced it or the user was
  //     already at the bottom — so reading older history isn't yanked down by a new
  //     or streaming turn.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const prepend = pendingPrependRef.current;
    if (prepend) {
      pendingPrependRef.current = null;
      el.scrollTop = prepend.prevScrollTop + (el.scrollHeight - prepend.prevScrollHeight);
      return;
    }
    if (forceBottomRef.current || stickToBottomRef.current) {
      forceBottomRef.current = false;
      // Behaviour is chosen deliberately, because `.chat` sets
      // `scroll-behavior: smooth` in CSS and this effect runs on EVERY token.
      // Assigning scrollTop under that CSS starts a smooth animation — and the
      // next token restarts it before it lands, so the view permanently trails
      // the text and stutters. While STREAMING we therefore scroll instantly:
      // the follow is exact, and the motion the reader sees is the text itself
      // rising, which is the effect we actually want. A discrete jump (sending,
      // opening a chat) still animates, where smooth reads as intentional.
      el.scrollTo({ top: el.scrollHeight, behavior: streaming ? 'instant' : 'smooth' });
    }
  }, [messages, streaming]);

  // Track bottom-stickiness and trigger older-message loading near the top.
  // Plain function (recreated each render) so it always closes over the latest
  // loadOlderMessages/state; loadOlderMessages self-guards (ref + hasMore + cursor).
  function onChatScroll(): void {
    const el = scrollerRef.current;
    if (!el) return;
    const fromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    stickToBottomRef.current = fromBottom < 80;
    // Scrolling up during a reply is a deliberate act — the user is re-reading
    // something. Auto-follow correctly stops, but without an affordance they are
    // then stranded: the answer keeps growing somewhere below with no way back
    // except dragging. Surface a return control instead.
    //
    // The trigger is the SAME condition as auto-follow, deliberately. It had its
    // own 200px threshold, which meant one concept — "is the reader at the live
    // end?" — was answered by two different numbers, so there was a dead band
    // where following had stopped but no way back was offered. One threshold, one
    // answer. Set only on CHANGE, so a scroll gesture doesn't re-render the thread
    // on every event.
    const away = !stickToBottomRef.current;
    if (away !== awayFromBottomRef.current) {
      awayFromBottomRef.current = away;
      setAwayFromBottom(away);
    }
    if (el.scrollTop < 160) void loadOlderMessages();
  }

  /** Jump back to the live end of the conversation and resume auto-follow. */
  function jumpToLatest(): void {
    const el = scrollerRef.current;
    if (!el) return;
    stickToBottomRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }

  // Scope filter for the chat-list rail. 'all' = every session (legacy);
  // a product slug ('esg' / 'lumara' / 'datacaffe' / 'electioncaffe') maps
  // to ChatSession.metadata.product on the server. Lets users see only the
  // chats started inside one product widget when they want.
  const [chatScope, setChatScope] = useState<ChatScope>(
    () => (localStorage.getItem('brewChatScope') as ChatScope) || 'all',
  );
  useEffect(() => { localStorage.setItem('brewChatScope', chatScope); }, [chatScope]);

  // Single entry point for the surface switcher + the "/" command menu.
  // connectorId === null → native Brew (scope 'all'). 'esg-caffe' → the ESG MFE
  // iframe. Any other linked product → native chat pinned to that connector
  // (connectorHint routing). The choice persists across reloads.
  const chooseSurface = useCallback((connectorId: string | null) => {
    if (connectorId === 'esg-caffe') {
      setError((prev) =>
        prev && /ESG embedded UI is unavailable/i.test(prev) ? null : prev,
      );
      if (ESG_NATIVE_CARDS) {
        // Native rendering: route ESG through the native chat (no iframe).
        setProductView(null);
        localStorage.setItem('brewProductView', 'native');
        setChatScope('esg');
      } else {
        setEsgIframeStatus('loading');
        setProductView('esg');
        localStorage.setItem('brewProductView', 'esg');
        setChatScope('esg');
      }
    } else {
      clearEsgIframeTimer();
      setEsgIframeStatus('idle');
      setProductView(null);
      localStorage.setItem('brewProductView', 'native');
      setChatScope(connectorId ? (CONNECTOR_TO_SCOPE[connectorId] ?? 'all') : 'all');
    }
  }, [clearEsgIframeTimer]);

  // Chat always routes through Brew Native — the backend prompt-routing engine
  // picks the right product per turn, so no product/surface picker in the UI.
  const activeProductId = productView === 'esg' && linkedConnectors.has('esg-caffe')
    ? 'esg-caffe'
    : (chatScope !== 'all' ? (SCOPE_TO_CONNECTOR[chatScope] ?? null) : null);

  // The backdrop ambient follows the ACTIVE WRAPPER — the brain the user is
  // actually talking to. Brew Native → neutral Native ambient; a product wrapper
  // (Lumara / ESG Caffe Expert / …) → that product's theme. It must NOT inherit a
  // lingering sidebar/scope product: otherwise picking "Brew Native" while a stale
  // chatScope='lumara' persists (e.g. restored from localStorage) still painted
  // Lumara's blinds. The ESG embedded-iframe surface is the one scope-driven
  // exception. The sidebar "selected" accent stays on the scope (activeProductId)
  // below, so filtering chats by product still highlights without changing the brain.
  const themeScope: ChatScope =
    wrapperToScope(selectedWrapper)
    ?? (productView === 'esg' && linkedConnectors.has('esg-caffe') ? 'esg' : 'all');

  // First page (30 rows) with a keyset cursor; subsequent pages load on scroll.
  const SESSIONS_PAGE = 30;
  const sessionsPath = useCallback((scope: ChatScope, cursor: { ts: number; id: string } | null) => {
    const p = new URLSearchParams();
    if (scope !== 'all') p.set('product', scope);
    p.set('limit', String(SESSIONS_PAGE));
    if (cursor) { p.set('before_ts', String(cursor.ts)); p.set('before_id', cursor.id); }
    return `/api/chat-sessions?${p.toString()}`;
  }, []);

  const refreshSessions = useCallback(async (scope: ChatScope = chatScope, opts?: { silent?: boolean }) => {
    // Background/auto refreshes run silent — no skeletons, no flicker — so the
    // list updates in place without yanking the user's scroll position. This
    // resets to page 1 (cheap now: 30 rows, no count scan).
    if (!opts?.silent) setSidebarLoading(true);
    try {
      // Retry a TRANSIENT failure (network blip / server restart / dev-proxy
      // race) so the sidebar reliably populates; stop only on a real 401/403.
      let r: SessionListResponse | null = null;
      let lastErr: unknown = null;
      for (let attempt = 0; attempt < 3 && !r; attempt++) {
        try {
          r = await api.get<SessionListResponse>(sessionsPath(scope, null));
        } catch (e) {
          lastErr = e;
          const status = (e as { status?: number } | null)?.status;
          if (status === 401 || status === 403) throw e; // genuine auth error — don't retry
          await new Promise((res) => setTimeout(res, 300 * (attempt + 1)));
        }
      }
      if (!r) throw lastErr;
      // Merge, don't hard-replace: if this refresh raced the persistence of the
      // CURRENTLY-ACTIVE chat (just created), the server's first page won't list it
      // yet — keep its optimistic row so a refresh can't make the live chat vanish
      // from the sidebar (#23). Only the active id is preserved, and only until the
      // server includes it, so no stale rows accumulate.
      const server = r.sessions;
      setSessions((prev) => {
        const activeId = activeIdRef.current;
        if (!activeId || server.some((s) => s.id === activeId)) return server;
        const optimistic = prev.find((s) => s.id === activeId);
        return optimistic ? [optimistic, ...server] : server;
      });
      setSessionsHasMore(!!r.hasMore);
      const last = r.sessions[r.sessions.length - 1];
      sessionsCursorRef.current = last ? { ts: last.updated_at_ms, id: last.id } : null;
    } catch (e) {
      console.warn('sidebar list failed', e);
    } finally {
      if (!opts?.silent) setSidebarLoading(false);
    }
  }, [chatScope, sessionsPath]);

  // Fetch the next server page and append (dedup by id) — driven by the sidebar's
  // bottom sentinel. Guarded so overlapping scroll events can't double-fetch.
  const loadMoreSessions = useCallback(async () => {
    if (sessionsLoadingMore || !sessionsHasMore) return;
    const cursor = sessionsCursorRef.current;
    if (!cursor) return;
    setSessionsLoadingMore(true);
    try {
      const r = await api.get<SessionListResponse>(sessionsPath(chatScope, cursor));
      setSessions((prev) => {
        const seen = new Set(prev.map((s) => s.id));
        return [...prev, ...r.sessions.filter((s) => !seen.has(s.id))];
      });
      setSessionsHasMore(!!r.hasMore);
      const last = r.sessions[r.sessions.length - 1];
      if (last) sessionsCursorRef.current = { ts: last.updated_at_ms, id: last.id };
      else setSessionsHasMore(false);
    } catch (e) {
      console.warn('sidebar load-more failed', e);
    } finally {
      setSessionsLoadingMore(false);
    }
  }, [chatScope, sessionsHasMore, sessionsLoadingMore, sessionsPath]);
  useEffect(() => { void refreshSessions(chatScope); }, [refreshSessions, chatScope]);

  // Persist the open chat so a refresh reopens it; cleared when there's no active
  // session (a new chat), so a refresh from there correctly stays on a new chat.
  useEffect(() => {
    if (activeId) localStorage.setItem('brewLastSessionId', activeId);
    else localStorage.removeItem('brewLastSessionId');
  }, [activeId]);
  // Restore the last open chat once, after wrappers have loaded. Silent: a stored
  // id whose session was deleted just falls back to a new chat, no error banner.
  useEffect(() => {
    if (didRestoreRef.current || !wrappersReady) return;
    didRestoreRef.current = true;
    const id = restoreSessionRef.current;
    restoreSessionRef.current = null;
    if (id) void loadSession(id, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrappersReady]);

  // Map a server message row → the client Msg shape (user/assistant only).
  function toMsg(m: FullSessionResponse['messages'][number]): Msg | null {
    if (m.role !== 'user' && m.role !== 'assistant') return null;
    const toolResults = toolResultsFromMeta(m.metadata);
    const imageUrl = (m.metadata as { imageUrl?: unknown } | null | undefined)?.imageUrl;
    // Agent-mode download chips persisted in metadata.files.
    const rawFiles = (m.metadata as { files?: unknown } | null | undefined)?.files;
    const files = Array.isArray(rawFiles)
      ? (rawFiles as unknown[]).filter(
          (f): f is AgenticFile =>
            !!f && typeof (f as AgenticFile).path === 'string' && typeof (f as AgenticFile).name === 'string',
        )
      : undefined;
    // Files the user attached to this turn (persisted in metadata.attachments).
    const rawAttach = (m.metadata as { attachments?: unknown } | null | undefined)?.attachments;
    const attachments = Array.isArray(rawAttach)
      ? (rawAttach as unknown[]).filter(
          (a): a is AttachedFile =>
            !!a && typeof (a as AttachedFile).fileId === 'string'
            && typeof (a as AttachedFile).path === 'string'
            && typeof (a as AttachedFile).name === 'string',
        )
      : undefined;
    return {
      id: m.id,
      role: m.role,
      content: m.content,
      ...(m.wrapper ? { wrapper: m.wrapper } : {}),
      ...(toolResults ? { toolResults } : {}),
      ...(typeof imageUrl === 'string' ? { imageUrl } : {}),
      ...(files && files.length > 0 ? { files } : {}),
      ...(attachments && attachments.length > 0 ? { attachments } : {}),
      // A turn whose generation failed is stored as an assistant message flagged
      // in metadata, so reopening the chat still shows what went wrong instead of
      // a question that appears to have been ignored.
      ...((m.metadata as { error?: unknown } | null | undefined)?.error === true ? { error: true } : {}),
    };
  }

  async function loadSession(id: string, opts?: { silent?: boolean }): Promise<void> {
    setError(null);
    // The preview pane shows a file from the CURRENT chat — switching to another
    // conversation makes it stale, so close it as we navigate away.
    setPreviewFile(null);
    // Show the message skeleton while this page loads (#4). A silent background
    // restore skips it — it shouldn't flash a skeleton over whatever is on screen.
    if (!opts?.silent) setMsgLoading(true);
    try {
      // Load only the most-recent page; scroll-up fetches older turns.
      // Clear any in-flight paging state so a stale prepend adjustment from the
      // previous chat can't fire against this one.
      loadingOlderRef.current = false;
      pendingPrependRef.current = null;
      const r = await api.get<FullSessionResponse>(`/api/chat-sessions/${id}?limit=${MESSAGES_PAGE}`);
      setActiveId(r.id);
      activeIdRef.current = r.id; // sync now (not post-paint) so an in-flight older-load sees the switch
      const loaded = r.messages.map(toMsg).filter((m): m is Msg => m !== null);
      setMessages(loaded);
      // Historical turns must never be re-planned. Without this, simply REOPENING a
      // chat whose last message is a long assistant answer re-runs the mission effect
      // — a wasted planner call, and a studio skeleton that flashes and vanishes with
      // no user action at all (missionPlan isn't persisted, so it can't be restored).
      for (const m of loaded) if (m.role === 'assistant') assistDoneRef.current.add(m.id);
      // The pinned mission belongs to the chat we just left.
      setActiveMissionId(null);
      setPlanningMissionId(null);
      // Rebuild the sticky attachment set from every turn's persisted
      // attachments so an uploaded doc stays available for follow-ups after a
      // reload / reopen. Newer wins on fileId collisions. Composer starts clear.
      const stickyById = new Map<string, AttachedFile>();
      for (const msg of loaded) for (const a of msg.attachments ?? []) stickyById.set(a.fileId, a);
      setSessionAttachments(Array.from(stickyById.values()));
      setPendingAttachments([]);
      // Cursor = the OLDEST loaded message (messages are ASC, so index 0).
      const oldest = r.messages[0];
      oldestMsgCursorRef.current = oldest ? { ts: oldest.ts ?? 0, id: oldest.id } : null;
      setMsgHasMoreOlder(!!r.hasMoreOlder && !!oldest);
      // A reopened chat opens pinned to the latest turn.
      stickToBottomRef.current = true;
      forceBottomRef.current = true;
      // Scope restore on reopen. History: pinning to the last AUTO-routed wrapper
      // made chats sticky (a follow-up got stuck on the wrong product and wouldn't
      // switch), so the default is Brew Native (re-identifies per turn).
      // BUT an explicitly-scoped SOLUTION should survive a reopen — the user chose
      // it, and the SolutionBar still lets them switch. So restore the last wrapper
      // ONLY when it's a solution the user currently has CONNECTED; anything else
      // (a persona, a disconnected/auto-routed product) falls back to General.
      const lastWrapper = [...r.messages].reverse()
        .find((m) => m.role === 'assistant' && m.wrapper)?.wrapper ?? null;
      const lastW = lastWrapper ? wrappers.find((x) => x.name === lastWrapper) : undefined;
      const restoreSolution =
        !!lastW && isSolutionWrapper(lastW) && linkedConnectors.has(solutionConnectorId(lastW));
      setSelectedWrapper(restoreSolution ? lastWrapper! : DEFAULT_WRAPPER);
      // Seed the tag tracker from the session's last product so re-opening it
      // doesn't redundantly re-PATCH the same tag on the next turn.
      taggedScopeRef.current = wrapperToScope(lastWrapper);
    } catch (e) {
      // Silent restore of a now-deleted session: drop the stale pointer and stay
      // on a new chat instead of flashing an error the user didn't trigger.
      if (opts?.silent) {
        localStorage.removeItem('brewLastSessionId');
        return;
      }
      setError((e as Error).message || 'Could not load chat');
    } finally {
      if (!opts?.silent) setMsgLoading(false);
    }
  }

  // Fetch the page of messages OLDER than the current cursor and PREPEND them. The
  // scroll position is preserved by the layout effect (pendingPrependRef) so the
  // viewport stays on the same turn instead of jumping. Guarded against overlap.
  async function loadOlderMessages(): Promise<void> {
    if (loadingOlderRef.current || !msgHasMoreOlder || !activeId) return;
    const startedForId = activeId;               // guard: bail if the session changes mid-fetch
    const startCursor = oldestMsgCursorRef.current;
    if (!startCursor) return;
    // Explicit type breaks a TS circular-inference cycle (cursor ← oldest ← r ← cursor).
    let cursor: { ts: number; id: string } = startCursor;
    loadingOlderRef.current = true;
    // Capture the read position NOW — synchronously, BEFORE setMsgLoadingOlder(true)
    // flushes the "Loading earlier…" row into the scroller — so the indicator's
    // height can't corrupt the restore delta. (React flushes at the first await.)
    const scroller = scrollerRef.current;
    const prevScrollHeight = scroller ? scroller.scrollHeight : 0;
    const prevScrollTop = scroller ? scroller.scrollTop : 0;
    setMsgLoadingOlder(true);
    try {
      // Accumulate across pages so a page containing ONLY non-displayable rows
      // (system/tool) doesn't prematurely end pagination — keep going, advancing the
      // cursor, until we collect some displayable turns or the history runs out.
      let collected: Msg[] = [];
      let hasMore = true;
      for (let guard = 0; guard < 25; guard += 1) {
        const r = await api.get<FullSessionResponse>(
          `/api/chat-sessions/${startedForId}?limit=${MESSAGES_PAGE}&before_ts=${cursor.ts}&before_id=${encodeURIComponent(cursor.id)}`,
        );
        // The user opened a different chat while this was in flight → abandon,
        // touching no state (else we'd prepend this session's rows onto that one).
        if (activeIdRef.current !== startedForId) { pendingPrependRef.current = null; return; }
        const oldest = r.messages[0];
        collected = [...r.messages.map(toMsg).filter((m): m is Msg => m !== null), ...collected];
        hasMore = !!r.hasMoreOlder && !!oldest;
        if (oldest) cursor = { ts: oldest.ts ?? 0, id: oldest.id };
        if (collected.length > 0 || !hasMore || !oldest) break;
      }
      if (collected.length === 0) { setMsgHasMoreOlder(hasMore); return; }
      // Arm the restore ref right before the prepend (so a streaming delta mid-fetch
      // can't consume it), using the pre-indicator capture above (so the indicator's
      // height doesn't skew the delta).
      if (scroller) pendingPrependRef.current = { prevScrollHeight, prevScrollTop };
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.id));
        return [...collected.filter((m) => !seen.has(m.id)), ...prev];
      });
      oldestMsgCursorRef.current = cursor;
      setMsgHasMoreOlder(hasMore);
    } catch (e) {
      console.warn('load older messages failed', e);
      pendingPrependRef.current = null;
    } finally {
      loadingOlderRef.current = false;
      setMsgLoadingOlder(false);
    }
  }

  function resetMessagePaging(): void {
    oldestMsgCursorRef.current = null;
    pendingPrependRef.current = null;
    setMsgHasMoreOlder(false);
    stickToBottomRef.current = true;
  }

  function newChat(): void {
    setActiveId(null);
    activeIdRef.current = null;
    setMessages([]);
    setInput('');
    setError(null);
    setPreviewFile(null);   // a fresh chat has no file to preview
    // Drop the pinned mission — it belongs to the chat we're leaving.
    setActiveMissionId(null);
    setPlanningMissionId(null);
    resetMessagePaging();
    taggedScopeRef.current = null;
    setSelectedWrapper(DEFAULT_WRAPPER); // new chat defaults to Brew Native
    // A fresh conversation starts with no attached files.
    setPendingAttachments([]);
    setSessionAttachments([]);
  }

  async function deleteSession(id: string): Promise<void> {
    try {
      await api.delete(`/api/chat-sessions/${id}`);
      if (activeId === id) newChat();
      await refreshSessions();
    } catch (e) {
      setError((e as Error).message || 'Delete failed');
    }
  }

  function rid(): string {
    return Math.random().toString(36).slice(2, 10);
  }

  async function ensureSession(provisionalTitle?: string): Promise<string> {
    if (activeId) return activeId;
    // Tag the new session with the active product scope so the dropdown's
    // per-product list shows it (the list filters by metadata.product). On the
    // 'all' (auto-route) scope it starts untagged and gets tagged with whatever
    // product the first turn routes to (see send()).
    const tag = chatScope !== 'all' ? chatScope : null;
    // Retry across a transient DB blip — if the session isn't created, NOTHING
    // this turn persists and the chat is lost on the next switch (bug #8).
    const created = await withRetry(() => api.post<SessionCreateResponse>('/api/chat-sessions', {
      mode: 'chat',
      ...(tag ? { metadata: { product: tag } } : {}),
    }));
    taggedScopeRef.current = tag;
    setActiveId(created.id);
    activeIdRef.current = created.id;
    // #13 — surface the new chat at the TOP of the sidebar the INSTANT it is
    // created, instead of waiting for the first append/reply to trigger a
    // refreshSessions round-trip. Prepend a provisional row (server assigns the
    // real title after the first turn; the next refreshSessions swaps it in). The
    // panel re-sorts by updated_at_ms desc, so `now` pins it first. Guard against a
    // double-insert if this id is somehow already present.
    const now = Date.now();
    const title = (provisionalTitle || '').replace(/\s+/g, ' ').trim().slice(0, 60) || 'New chat';
    setSessions((prev) => (prev.some((s) => s.id === created.id)
      ? prev
      : [{ id: created.id, title, mode: 'chat', created_at_ms: now, updated_at_ms: now }, ...prev]));
    return created.id;
  }

  // Friendly, human label for an agent tool_call — what the build is doing right
  // now. Mirrors the server's NATIVE_TOOL_LABELS and names the file when the tool
  // carries one ("Writing index.html"), so the trail reads like work, not tool ids.
  function agentStepLabel(name: unknown, input: unknown): string {
    const t = (typeof name === 'string' ? name : '').toLowerCase();
    const i = (input && typeof input === 'object' ? input : {}) as Record<string, unknown>;
    const rawPath = i.path ?? i.filename ?? i.file ?? i.file_path ?? i.target_file;
    const file = typeof rawPath === 'string' ? rawPath.split('/').pop() : '';
    if (t.includes('web_search') || t.includes('search')) return 'Searching the web';
    if (t.includes('fetch') || t.includes('browse') || t.includes('url') || t.includes('scrape') || t.includes('crawl')) return 'Fetching a page';
    if (t.includes('write_file') || t === 'write' || t.includes('create_file')) return file ? `Writing ${file}` : 'Writing a file';
    if (t.includes('edit') || t.includes('str_replace') || t.includes('apply_patch')) return file ? `Editing ${file}` : 'Editing a file';
    if (t.includes('read_file') || t === 'read' || t.includes('view')) return file ? `Reading ${file}` : 'Reading a file';
    if (t.includes('bash') || t.includes('terminal') || t.includes('shell') || t.includes('exec') || t.includes('run')) return 'Running a command';
    if (t.includes('skill')) return 'Loading a skill';
    if (t.includes('image') || t.includes('render') || t.includes('chart') || t.includes('plot')) return 'Rendering a visual';
    return t ? `Working: ${t.replace(/_/g, ' ')}` : 'Working…';
  }

  // Drive ONE agentic turn through the web-gateway's Hermes bridge. Opens a
  // short-lived SSE stream on /api/events, fires the message via /api/send once
  // the stream is live, and translates the brewcode-protocol events into the
  // streaming assistant bubble. Files the agent creates are collected from
  // tool_result outputs and rendered as download chips. Resolves on
  // turn_complete or error.
  async function runAgenticTurn(
    text: string,
    sid: string,
    assistantMsgId: string,
    token: string | null,
    attachments: AttachedFile[] = [],
    newFileIds: Set<string> = new Set(),
    recentContext = '',
  ): Promise<void> {
    // Badge the bubble as the Agent so it reads "Brew · Agent".
    // Name the agent that actually ran. Both used to render 'Breww · Agent',
    // so a Builder reply and an Assistant reply were indistinguishable — which
    // is precisely how a Hermes-written file got mistaken for BrewCode's.
    setMessages((m) => m.map((msg) => (msg.id === assistantMsgId ? { ...msg, wrapper: agentLabel } : msg)));

    // A turn now owns the bridge — stop() must send it an `interrupt`, not just
    // abort a fetch the bridge never sees. Cleared in finish().
    agentTurnRef.current = true;

    const esUrl = `/api/events${token ? `?token=${encodeURIComponent(token)}` : ''}`;
    const es = new EventSource(esUrl);
    // Keyed by FILE NAME, not path. The same file arrives from two sources in
    // two different shapes: a tool_result reports an ABSOLUTE path
    // (/workspace/<user>/x.html) while the gateway's workspace diff reports a
    // workspace-RELATIVE one (x.html). Keying by path rendered BOTH — two
    // cards for one file. /api/preview resolves either shape, so first wins.
    const filesByPath = new Map<string, AgenticFile>();
    const addFile = (raw: string, role?: AgenticFile['role'], artifactType?: string) => {
      const name = raw.split(/[\\/]/).pop() || raw;
      if (!filesByPath.has(name)) filesByPath.set(name, { path: raw, name, ...(role ? { role } : {}), ...(artifactType ? { artifactType } : {}) });
    };
    let assistantText = '';
    let settled = false;
    let stepSeq = 0;   // fallback ids for tool_call steps that arrive without one

    const attachFiles = () => {
      if (filesByPath.size === 0) return;
      const files = Array.from(filesByPath.values());
      setMessages((m) => m.map((msg) => (msg.id === assistantMsgId ? { ...msg, files } : msg)));
    };

    await new Promise<void>((resolve) => {
      const finish = (errMsg?: string) => {
        if (settled) return;
        settled = true;
        agentTurnRef.current = false;
        try { es.close(); } catch { /* already closed */ }
        if (errMsg) setError(errMsg);
        attachFiles();
        // Persist the assistant turn so it rehydrates on reload (text + files).
        if (assistantText.trim() || filesByPath.size > 0) {
          void withRetry(() => api.post(`/api/chat-sessions/${sid}/append`, {
            role: 'assistant',
            content: assistantText,
            wrapper: agentLabel,
            ...(filesByPath.size > 0 ? { metadata: { files: Array.from(filesByPath.values()) } } : {}),
          })).catch(() => { /* non-fatal */ });
        }
        resolve();
      };

      es.onopen = () => {
        // Stream is live — dispatch the message. Its events arrive on this stream.
        void fetch('/api/send', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
          body: JSON.stringify({
            type: 'user_message', agent, text,
            // Which Breww chat this turn belongs to. The bridge keys its response
            // chain to this so switching chats starts a fresh agent conversation —
            // without it, a prior chat stays chained and "create a pdf of this"
            // resolves to another conversation's content.
            session_id: sid,
            // Recent turns of THIS chat, so a demonstrative ("create a pdf of this")
            // resolves to the current conversation even when that content was
            // produced on the plain-chat path the agent never saw.
            ...(recentContext ? { context: recentContext } : {}),
            // Sticky conversation attachments — the bridge injects these into the
            // turn so the agent can read/analyze them for ANY prompt. `isNew`
            // marks files attached THIS turn: only those images get inlined as
            // pixels (a sticky image would re-send megabytes every turn → 413).
            ...(attachments.length > 0
              ? { attachments: attachments.map((a) => ({ name: a.name, kind: a.kind, path: a.path, sizeBytes: a.sizeBytes, isNew: newFileIds.has(a.fileId) })) }
              : {}),
          }),
        }).catch((e) => finish(`Failed to reach the agent: ${(e as Error).message}`));
      };

      es.onerror = () => {
        // EventSource auto-reconnects; only fatal if closed before we settled.
        if (!settled && es.readyState === EventSource.CLOSED) finish('Lost connection to the agent stream.');
      };

      es.onmessage = (ev: MessageEvent) => {
        let obj: {
          type?: string; text?: string; output?: unknown; message?: string;
          agent?: string; state?: string; files?: unknown[];
          id?: string; name?: string; input?: unknown;
          model?: string | null; source?: string;
        };
        try { obj = JSON.parse(ev.data as string); } catch { return; }
        switch (obj?.type) {
          // Which model the bridge requested for this turn (build-override or a
          // house-skill pin). Surfaced as a small tag on the reply so the model
          // used is trackable in the UI instead of only in server logs.
          case 'model_info': {
            const mdl = typeof obj.model === 'string' && obj.model ? obj.model : null;
            if (mdl) setMessages((m) => m.map((msg) => (msg.id === assistantMsgId ? { ...msg, model: mdl } : msg)));
            break;
          }
          // Per-agent bridge lifecycle. Emitted on start ('warming'), on the
          // bridge's real ready, and on teardown; also replayed when this stream
          // opens, so a reload re-syncs from the live bridges.
          case 'engine_status': {
            const a = obj.agent === 'brewcode' || obj.agent === 'hermes' ? obj.agent : null;
            const st = obj.state;
            if (a && (st === 'warming' || st === 'ready' || st === 'stopped')) {
              setEngineState((s) => ({ ...s, [a]: st }));
            }
            break;
          }
          case 'assistant_text': {
            const delta = typeof obj.text === 'string' ? obj.text : '';
            if (delta) {
              assistantText += delta;
              setMessages((m) => m.map((msg) => (msg.id === assistantMsgId ? { ...msg, content: msg.content + delta } : msg)));
            }
            break;
          }
          case 'tool_call': {
            // The AGENT'S OWN PLAN: the `todo` tool carries {todos:[{id,content,
            // status}]} — mirror it into msg.plan (the checklist) with the agent's
            // real statuses, rather than guessing deliverables from the prompt.
            // Detected by payload shape (name-agnostic). Merge by id so a partial
            // (merge-mode) update doesn't drop the other items.
            const todos = (obj.input && typeof obj.input === 'object'
              && Array.isArray((obj.input as { todos?: unknown }).todos))
              ? (obj.input as { todos: Array<Record<string, unknown>> }).todos : null;
            if (todos) {
              const norm = (s: unknown): 'pending' | 'in_progress' | 'completed' | 'cancelled' => {
                const v = String(s ?? 'pending').toLowerCase();
                return v === 'in_progress' || v === 'completed' || v === 'cancelled' ? v : 'pending';
              };
              setMessages((m) => m.map((msg) => {
                if (msg.id !== assistantMsgId) return msg;
                const byId = new Map((msg.plan ?? []).map((p) => [p.id, p]));
                for (const t of todos) {
                  const tid = String(t.id ?? t.content ?? '').trim();
                  if (!tid) continue;
                  const prev = byId.get(tid);
                  byId.set(tid, { id: tid, label: String(t.content ?? prev?.label ?? '').trim(), status: norm(t.status ?? prev?.status) });
                }
                return { ...msg, plan: [...byId.values()] };
              }));
              break;   // the plan renders it — don't also add a generic "todo" step
            }
            // Any other tool → a live step so a build shows its work.
            const id = typeof obj.id === 'string' && obj.id ? obj.id : `s${stepSeq++}`;
            const label = agentStepLabel(obj.name, obj.input);
            setMessages((m) => m.map((msg) => (msg.id === assistantMsgId
              ? { ...msg, agentSteps: [...(msg.agentSteps ?? []), { id, label, done: false }] }
              : msg)));
            break;
          }
          case 'tool_result': {
            // Mark the matching step done (by id, else the last running one).
            setMessages((m) => m.map((msg) => {
              if (msg.id !== assistantMsgId || !msg.agentSteps?.length) return msg;
              const steps = msg.agentSteps;
              const idx = obj.id ? steps.findIndex((s) => s.id === obj.id && !s.done)
                                 : steps.map((s) => s.done).lastIndexOf(false);
              if (idx < 0) return msg;
              const next = steps.slice();
              next[idx] = { ...next[idx]!, done: true };
              return { ...msg, agentSteps: next };
            }));
            // Pull created/modified file paths out of the tool's JSON output.
            try {
              const out = (typeof obj.output === 'string' ? JSON.parse(obj.output) : obj.output) as
                { files_modified?: unknown; resolved_path?: unknown };
              const paths: string[] = Array.isArray(out?.files_modified)
                ? (out.files_modified as unknown[]).filter((x): x is string => typeof x === 'string')
                : (typeof out?.resolved_path === 'string' ? [out.resolved_path] : []);
              for (const p of paths) addFile(p);
              attachFiles();
            } catch { /* not a file-op result — ignore */ }
            break;
          }
          // Files the agent created/edited this turn, from the gateway's
          // post-turn workspace diff. Agent-agnostic on purpose: it watches the
          // FILESYSTEM, so it catches files whatever tool shape an engine emits
          // (BrewCode's tool_result doesn't look like Hermes', so nothing was
          // anchoring a card and the reply said "I created page.html" with no
          // way to open it).
          case 'files_changed': {
            const list = Array.isArray(obj.files) ? obj.files : [];
            for (const f of list) {
              const rel = (f as { path?: unknown })?.path;
              if (typeof rel !== 'string' || !rel) continue;
              const role = (f as { role?: AgenticFile['role'] })?.role;
              const artifactType = (f as { artifactType?: string })?.artifactType;
              addFile(rel, role, artifactType);
            }
            if (list.length) attachFiles();
            break;
          }
          case 'error': {
            finish(typeof obj.message === 'string' ? obj.message : 'Agent error');
            break;
          }
          case 'turn_complete': {
            finish();
            break;
          }
          default:
            break;
        }
      };
    });
  }

  // `promptOverride` lets an interactive card (SelectionCard) send a choice on
  // the user's behalf without going through the composer input.
  async function send(
    promptOverride?: string,
    opts?: {
      connectorHint?: string;
      forceWrapper?: string;
      cardReply?: { cardId: string; ownerConnectorId: string; value: string };
      historyOverride?: Msg[];
      /** This turn is SELF-CONTAINED text — the caller already put everything the
       *  model needs into the prompt (e.g. the Quick-analysis profile). Two
       *  consequences: don't attach the pending file, and never route to the
       *  agent. Both are required — dropping the attachment alone isn't enough,
       *  because the SLM classifier still sees "analyse this…" plus a STICKY
       *  upload and sends the turn to Hermes, which such a turn doesn't need. */
      plainChat?: boolean;
      /** This turn came from the hands-free voice mode — reply short + spoken-style
       *  (a system directive is injected so the answer sounds natural read aloud). */
      voiceReply?: boolean;
      /** A Mission OS deliverable/action click. 'build' guarantees the agentic
       *  builder runs (a deliverable must produce a real artifact, never a chat
       *  paragraph — the studio's whole promise). Deterministic: it does not
       *  gamble on the SLM intent classifier. */
      missionRoute?: 'build' | 'chat';
    },
  ): Promise<void> {
    const trimmed = (promptOverride ?? input).trim();
    if (!trimmed || streaming) return;
    setError(null);
    // When editing the last prompt, the turn is re-sent against the history
    // BEFORE that prompt (the old user+assistant turn was truncated). Everything
    // below reads `history` instead of `messages` so the model, the intent
    // classifier, and the optimistic bubble all see the truncated conversation.
    const history = opts?.historyOverride ?? messages;

    // Attachments: the files newly attached this turn (pending chips) plus the
    // conversation's sticky set. `turnAttachments` = new-this-turn (shown on the
    // user bubble + persisted). `stickyAttachments` = the full set sent to the
    // agent so it can answer follow-ups about any previously-uploaded file.
    // `skipAttachments` = the caller has ALREADY turned the file into text in the
    // prompt (the Quick-analysis profile), so this turn needs no file access.
    // Without it the attachment forces the agentic path (hasNewAttachment below)
    // and the turn dies on an unreachable Hermes — for an analysis the model
    // could answer from the prompt alone. Read from opts, not state: a
    // setPendingAttachments([]) right before send() would still be stale here.
    const turnAttachments = opts?.plainChat ? [] : pendingAttachments;
    const stickyAttachments = (() => {
      const byId = new Map(sessionAttachments.map((a) => [a.fileId, a]));
      for (const a of turnAttachments) byId.set(a.fileId, a);
      return Array.from(byId.values());
    })();

    // Optimistic UI first — show the message and clear the box the instant
    // Enter is pressed so sending feels snappy. Session creation and the
    // network round-trips happen afterwards; a slow create no longer blocks
    // the input from clearing or the bubble from appearing.
    const userMsg: Msg = {
      id: rid(), role: 'user', content: trimmed,
      ...(turnAttachments.length > 0 ? { attachments: turnAttachments } : {}),
    };
    // Open a deliverable turn on a real, format-aware status ("Creating your PDF…")
    // instead of the generic "Still brewwing" rotation — so a slow build reads as
    // working, not stuck. The agent's own tool steps ("Writing report.pdf") replace
    // this the moment they arrive; this only fills the thinking gap before them.
    const startLabel: string | undefined = (() => {
      if (looksLikeAgentTask(trimmed) !== 'create_file') return undefined;
      const p = trimmed.toLowerCase();
      if (/\bpdf\b/.test(p)) return 'Creating your PDF';
      if (/\b(pptx?|power\s?point|slides?|deck|presentation)\b/.test(p)) return 'Building your deck';
      if (/\b(xlsx?|excel|spread\s?sheet|workbook|csv)\b/.test(p)) return 'Creating your spreadsheet';
      if (/\b(docx?|word\s?doc\w*)\b/.test(p)) return 'Writing your document';
      if (/\b(html?|web\s?page|landing\s?page|dashboard|website|web\s?app|ui)\b/.test(p)) return 'Designing your page';
      return 'Creating your file';
    })();
    // No prompt-guessed plan — the checklist is populated live from the agent's
    // own `todo` tool calls (see the 'tool_call' handler), so it reflects what the
    // agent actually decided to do, not a keyword guess.
    const assistantMsg: Msg = { id: rid(), role: 'assistant', content: '', ...(startLabel ? { progress: startLabel, isBuild: true } : {}) };
    forceBottomRef.current = true; // sending snaps to the latest turn
    setMessages([...history, userMsg, assistantMsg]);
    setStreamingId(assistantMsg.id);
    if (promptOverride === undefined) setInput('');
    setStreaming(true);
    // These files are now part of the conversation; clear the composer chips
    // and promote them into the sticky set.
    if (turnAttachments.length > 0) {
      setSessionAttachments(stickyAttachments);
      setPendingAttachments([]);
    }

    let sid: string;
    try {
      sid = await ensureSession(trimmed);
    } catch (e) {
      setError((e as Error).message);
      setStreaming(false);
      setStreamingId(null);
      return;
    }
    // Any activity bumps the active chat to the TOP of Recent immediately — the
    // sidebar sorts by updated_at_ms, so this keeps the chat you're using first
    // without waiting on the server refresh (which can race the append). Fixes a
    // new/active chat sometimes appearing mid-list.
    setSessions((prev) => prev.map((s) => (s.id === sid ? { ...s, updated_at_ms: Date.now() } : s)));

    // Persist the user turn. On the first message the server returns a freshly
    // generated sidebar title — refresh the list so it appears immediately,
    // rather than waiting on (and racing) the post-stream refresh below.
    // Attachments ride in metadata so they rehydrate (and stay sticky) on reload.
    void withRetry(() => api.post<{ title?: string }>(`/api/chat-sessions/${sid}/append`, {
      role: 'user', content: trimmed,
      ...(turnAttachments.length > 0 ? { metadata: { attachments: turnAttachments } } : {}),
    }))
      .then((r) => { if (r?.title) void refreshSessions(); })
      // After withRetry exhausts, the message genuinely didn't persist — say so
      // instead of swallowing it, so a lost turn is visible, not a silent delete (#23).
      .catch(() => setError('Couldn’t save your last message to this chat — it’s shown here, but may not reappear after a reload. Check your connection.'));

    const ctl = new AbortController();
    abortRef.current = ctl;
    let assistantText = '';
    let finalWrapper: string | null = null;
    let assistantToolResults: ToolResultSidecar[] | undefined;
    // Reasoning-model chain-of-thought (delta.reasoning). Streamed into the
    // collapsible Thinking block; `thinkingStart` stamps the first reasoning
    // token so we can show "Thought for Ns" once the answer begins.
    let thinkingStart: number | null = null;
    let thinkingStamped = false;
    // Streaming <think>…</think> peeler. Qwen3 / DeepSeek-R1 (and many open models)
    // emit their REAL reasoning as inline tags in the CONTENT stream rather than a
    // separate `reasoning` field — so without this it either leaks into the answer
    // or is lost and the UI falls back to a fake spinner. This routes the tagged
    // reasoning into the Thinking panel and keeps the answer clean. State persists
    // across chunks; `carry` holds a tag split across a chunk boundary ("…<thi").
    const thinkState = { inside: false, carry: '' };
    const peelThink = (chunk: string): { think: string; answer: string } => {
      const text = thinkState.carry + chunk;
      thinkState.carry = '';
      let think = '';
      let answer = '';
      let i = 0;
      while (i < text.length) {
        const tag = thinkState.inside ? '</think>' : '<think>';
        const hit = text.indexOf(tag, i);
        if (hit === -1) {
          // No full tag ahead — emit up to a POSSIBLE partial tag at the tail and
          // hold that partial back so a tag split across chunks still matches.
          const tail = text.slice(i);
          let keep = 0;
          for (let k = Math.min(tail.length, tag.length - 1); k > 0; k--) {
            if (tag.startsWith(tail.slice(tail.length - k))) { keep = k; break; }
          }
          const emit = text.slice(i, text.length - keep);
          if (thinkState.inside) think += emit; else answer += emit;
          thinkState.carry = keep ? text.slice(text.length - keep) : '';
          break;
        }
        const emit = text.slice(i, hit);
        if (thinkState.inside) think += emit; else answer += emit;
        thinkState.inside = !thinkState.inside;
        i = hit + tag.length;
      }
      return { think, answer };
    };

    try {
      const token = getToken();
      // ── Intent-based routing ────────────────────────────────────────────
      // Route to the Hermes agentic engine (tools, file creation, uploaded-doc
      // analysis) when EITHER:
      //   • the user attached a file THIS turn (they clearly want it processed), OR
      //   • the prompt is an UNAMBIGUOUS "create a pdf / make an excel / generate
      //     an image" (typo-tolerant fast-path), OR
      //   • the SLM classifier says the prompt needs the agent.
      // Crucially, a file attached in an EARLIER turn does NOT force every later
      // turn to the agent — otherwise "hi" or "capital of usa?" after an upload
      // would wrongly hit the agent. Sticky files only raise `hasUploads` so the
      // classifier can pick analyze_upload when the prompt is actually about them.
      const hasNewAttachment = turnAttachments.length > 0;
      // File creation is agentic. Image-generation intent must NOT go to the
      // Hermes agent (no image tool → it would just describe the image in prose);
      // 'generate_image' falls through to /api/chat, where the server image
      // short-circuit (looksLikeImageGenRequest → runImageGeneration) fires and
      // returns a real generated image. Explicitly picking the Brew Image wrapper
      // is an unconditional image-gen intent — route to /api/chat (skip the agent
      // + classifier); the server forces gen for requestedModel==='brew-imagen'.
      // Picking the Builder ALWAYS takes the agentic path: it only exists as a
      // bridge, so letting the classifier drop a Builder turn into plain chat
      // would answer as the Assistant while the UI still said Builder. The image
      // wrapper still wins — it's an explicit, unconditional image intent, and
      // the Builder has no image tool either.
      const imageWrapper = selectedWrapper === 'brew-imagen';
      // A Mission OS 'build' click must produce a real artifact. If the prompt
      // isn't obviously a file task, default it to create_file so goAgentic below
      // turns true deterministically (skipping the classifier gamble).
      const forceBuild = opts?.missionRoute === 'build';
      const agentTask = looksLikeAgentTask(trimmed) ?? (forceBuild ? 'create_file' : null);
      // Follow-ups on a just-generated image. Without these, the agentic-intent
      // classifier misreads "make it blue" as a file task (→ Hermes writes a .txt),
      // and a question ("what is this") under the image wrapper gets generated as an
      // image (→ a refusal). So we pin:
      //   • a REFINE ("make it yellow") to the image path (server merges subject+edit);
      //   • a QUESTION ("what is this") to a TEXT answer (model='auto' below), where
      //     the artifact grounding note answers it.
      const priorImage = hasRecentGeneratedImage(history);
      const isImageRefine = priorImage && agentTask == null && looksLikeImageRefinement(trimmed);
      const isImageQuestion = priorImage && !isImageRefine && agentTask == null && looksLikeImageQuestion(trimmed);
      const priorFile = hasRecentGeneratedFile(history);
      // A concept diagram ("flowchart of the login process", "diagram of the
      // Pythagorean theorem") is pinned to /api/chat, where the chat model renders
      // an inline mermaid diagram — NOT the Hermes agent, which dropped an
      // unnecessary .excalidraw/.html file or answered in prose. Builder (brewcode)
      // and explicit-file asks are excluded (looksLikeConceptDiagram handles the
      // latter), so real diagram-file deliverables still go agentic.
      const isConceptDiagram = agent !== 'brewcode' && agentTask == null && !isImageRefine
        && !isImageQuestion && looksLikeConceptDiagram(trimmed);
      // STICKY WORKSPACE. The agent's workspace is /workspace/<userId> — a
      // PERSISTENT per-user dir shared by every turn (gateway-server ctxFor +
      // workspaceDirFor), NOT "fresh per turn" as the old routing assumed. So once
      // this conversation has produced a real (non-image) file, its follow-ups —
      // whether a QUESTION ("where is it?", "what's in it?") or an EDIT ("make it
      // longer", "add a section") — go to the AGENT, which can actually open and
      // modify that file. The old path sent doc questions to text-only /api/chat,
      // where a model with no workspace answered "I'm a text-based AI, I can't
      // provide files" and dumped raw HTML. Phrasing no longer decides this; file
      // presence does. Bounded to the recent window (hasRecentGeneratedFile), so
      // stickiness lapses naturally as the topic moves on. Image intents keep their
      // own routing; a concept-diagram ask is a new inline artifact, not a
      // follow-up about the file, so it stays on chat.
      const isWorkspaceFollowUp = agent !== 'brewcode' && priorFile && agentTask == null
        && !isImageRefine && !isImageQuestion && !isConceptDiagram;
      // `plainChat` short-circuits BOTH agentic doors: the direct conditions here
      // and the SLM classifier below. A self-contained turn (the analysis profile
      // is already in the prompt) must never be handed to the agent.
      let goAgentic = !opts?.plainChat && !imageWrapper && !isImageRefine && !isImageQuestion && !isConceptDiagram
        && (agent === 'brewcode' || hasNewAttachment || agentTask === 'create_file' || isWorkspaceFollowUp);
      // Skip the agentic-intent classifier when: a concept diagram (routes to
      // /api/chat for inline mermaid), a plainChat turn, OR a plainly conversational
      // turn (staging's "don't pay an LLM to classify 'hi'" perf win) — all keep an
      // ordinary turn off the agent without a needless classifier round-trip.
      if (!opts?.plainChat && !imageWrapper && !isImageRefine && !isImageQuestion
          && !isConceptDiagram && !goAgentic && agentTask !== 'generate_image' && !clearlyNotAgentTask(trimmed)) {
        // Best-effort SLM route decision, capped at 4s so a slow/unconfigured
        // classifier never stalls a normal chat turn (we just fall back to chat).
        try {
          const recent = history.slice(-6).map((m) => `${m.role}: ${m.content.slice(0, 200)}`).join('\n');
          const ac = new AbortController();
          // Live voice turns need to stay snappy: cap the classifier tighter (a miss just falls
          // through to plain chat, which is where a conversational spoken turn belongs anyway).
          const t = setTimeout(() => ac.abort(), opts?.voiceReply ? 1200 : 4000);
          const r = await fetch('/api/agentic-intent', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
            body: JSON.stringify({ prompt: trimmed, recent, hasUploads: stickyAttachments.length > 0 }),
            signal: ac.signal,
          }).finally(() => clearTimeout(t));
          if (r.ok) {
            const intent = await r.json() as { agentic?: boolean };
            goAgentic = !!intent.agentic;
          }
        } catch { /* classifier is best-effort — fall back to chat on any failure */ }
      }
      if (goAgentic) {
        const newFileIds = new Set(turnAttachments.map((a) => a.fileId));
        // Recent context of THIS chat so the agent can resolve "this"/"it" to the
        // current conversation (esp. content produced on the plain-chat path it
        // never saw). Window widened from 6→20 messages / 8k→24k chars: in
        // STATELESS mode (server flag HERMES_STATELESS_TURNS) this is the ONLY
        // history the model sees — there is no previous_response_id chain — so it
        // must carry enough to keep multi-turn conversations coherent. Still
        // bounded so the request stays reasonable; very long chats will need
        // summarisation of older turns (future work). Harmless when the server is
        // in stateful mode: the bridge only injects it on turn 1 there.
        const recentCtx = history
          .filter((m) => (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string' && m.content.trim())
          .slice(-20)
          .map((m) => `${m.role === 'user' ? 'User' : 'Assistant'}: ${m.content.slice(0, 2000)}`)
          .join('\n\n')
          .slice(0, 24000);
        await runAgenticTurn(trimmed, sid, assistantMsg.id, token, stickyAttachments, newFileIds, recentCtx);
        void refreshSessions();
        void refreshUsage();
        return; // the outer finally resets streaming state
      }
      // "Answer here anyway" from the switch card → force the current wrapper and
      // lock the intent so the mismatch check doesn't fire again.
      const forceWrapper = opts?.forceWrapper;
      // The connector pin for THIS turn comes ONLY from an explicit product-pick
      // CLICK (a switch card / disambiguation button → opts.connectorHint). We
      // deliberately do NOT derive a pin from the sidebar chatScope anymore: Brew
      // Native must AUTO-IDENTIFY the right product per turn, and a lingering scope
      // (e.g. one restored from a previous session as 'esg') was silently pinning
      // EVERY native turn to that product — the "selected Lumara but got the ESG
      // profile" bug. The scope now only filters the chat list, never the routing.
      const connectorHintForTurn = forceWrapper ? undefined : opts?.connectorHint;
      // A card-option CLICK (Part E, rule #1). Carries the card's owner + id so the
      // server can validate it against the active card and pin the turn straight
      // back to that product — never re-classified as a bare prompt.
      const cardReply = opts?.cardReply;
      // The product this conversation is CURRENTLY with — taken from the last
      // assistant turn's wrapper. Sent as a SOFT hint (`activeConnector`, NOT a
      // hard `connectorHint`) so a follow-up / confirmation ("yes go ahead",
      // "in november") stays on the active product and Brew forwards the full
      // history to it — the same way the product's own chat widget keeps a
      // multi-turn flow (create customer → details → confirm → create) in one
      // place. A clear brand mention / distinctive signal still re-routes, since
      // the backend only uses this to seed soft affinity. Skipped when a hard pin
      // is already in play (explicit click or "answer here anyway").
      const lastAssistantWrapper = [...history].reverse()
        .find((m) => m.role === 'assistant' && m.wrapper)?.wrapper ?? null;
      const activeConnector = (!forceWrapper && !connectorHintForTurn && lastAssistantWrapper)
        ? (SCOPE_TO_CONNECTOR[wrapperToScope(lastAssistantWrapper) ?? 'all'] ?? null)
        : null;
      // Files this conversation generated in recent agentic turns. The gateway
      // (which alone can read the workspace) extracts a text digest from each so
      // the engine can ground a follow-up like "what was in that report?" on the
      // actual contents — those files are NOT in the replayed message text.
      const recentGeneratedFiles = (() => {
        const seen = new Set<string>();
        const out: Array<{ name: string; path: string }> = [];
        for (let i = history.length - 1; i >= 0 && out.length < 5; i--) {
          const files = history[i]?.files;
          if (!Array.isArray(files)) continue;
          for (const f of files) {
            if (f && typeof f.name === 'string' && typeof f.path === 'string' && !seen.has(f.name)) {
              seen.add(f.name);
              out.push({ name: f.name, path: f.path });
            }
          }
        }
        return out;
      })();
      // The gateway exposes /api/chat (not /api/v1/chat/completions). It
      // proxies to the backend's OpenAI-shape endpoint, adds the model
      // picker, and streams SSE chunks back. Body is { messages, model,
      // priority } — `stream: true` is forced upstream.
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          // Routing model:
          //  - product-pick click (opts.connectorHint) or product-scope dropdown
          //    → send the default wrapper + connectorHint so the engine PINS that
          //    product (the capability gate is skipped, no re-disambiguation).
          //  - native/all scope, nothing pinned → send 'auto' so the engine's
          //    capability gate routes to the product that can answer (or asks).
          //  - explicit "/" wrapper pick → send it verbatim.
          // A card reply ALWAYS routes 'auto' — the server pins it to the card
          // owner (rule #1), which requires the auto path (the pin runs there).
          // A QUESTION about a prior generated image ALSO forces 'auto' so it's
          // answered as text (with grounding) instead of the image wrapper trying
          // to generate an image of the question.
          model: isImageQuestion
            ? 'auto'
            : cardReply
            ? 'auto'
            : forceWrapper
              ? forceWrapper
              : connectorHintForTurn
                ? selectedWrapper
                // Brew Native ALWAYS auto-routes (engine picks the product per turn,
                // regardless of any sidebar scope); any other wrapper is an explicit
                // choice and is sent verbatim.
                : (selectedWrapper === DEFAULT_WRAPPER ? 'auto' : selectedWrapper),
          priority: 'balanced',
          // The conversation this turn belongs to. Without it the server's
          // extractSessionId falls back to `br_<userId>`, so EVERY chat of a
          // given user shared one key — a form opened in one chat was answerable
          // in another, a product pinned in one leaked into all of them, and the
          // rolling summary could not run at all (it has no session row to write
          // to). `sid` is awaited above and the send returns early if it fails,
          // so it is always a real ChatSession id by this point.
          session_id: sid,
          messages: [
            // Live voice turns: answer the way people actually speak — short, natural,
            // no markup (it sounds wrong read aloud). Faster to generate + synthesize too.
            ...(opts?.voiceReply ? [{
              role: 'system' as const,
              content:
                'You are in a LIVE VOICE conversation — the user HEARS you speak, so talk like a real ' +
                'person, not a document. Keep it brief and natural, usually 1–3 short sentences, and get to ' +
                'the point. Let a little humanity through: now and then open with a light, natural touch ' +
                '("hmm,", "right,", "got it —", "oh nice,", "yeah,") and gently mirror the user\'s energy and ' +
                'mood — warmer and calmer if they sound down or stressed, brighter if they\'re upbeat. Express ' +
                'ALL of this with ordinary spoken words only: never write stage directions, and NEVER use ' +
                'asterisks or square brackets like *sighs* or [laughs] — those get read aloud literally. Use ' +
                'plain conversational language only — NO markdown, headings, bullet or numbered lists, tables, ' +
                'code blocks, emojis, or URLs, since they are read aloud. ' +
                'IMPORTANT — the user\'s message is LIVE speech-to-text, so it may be slightly misheard, ' +
                'mis-split, or cut off mid-thought. Read for the INTENDED MEANING, not the literal words, and ' +
                'quietly look past obvious recognition slips (a wrong homophone, a dropped or duplicated word, ' +
                'odd spacing). If their intent is clear enough, just answer it naturally. But if a KEY detail is ' +
                'genuinely garbled, self-contradictory, or the sentence clearly breaks off unfinished, do NOT ' +
                'guess and confidently answer the wrong thing — ask ONE short, friendly clarifying question ' +
                '("sorry, did you mean …?", "could you say that last bit again?") and never invent specifics ' +
                'you didn\'t actually catch. When you do understand, don\'t nag with needless clarifiers. ' +
                'If a full answer needs detail, give the key point first and offer to go deeper. Match the ' +
                'user\'s language.',
            }] : []),
            // Concept-diagram turns: force an INLINE mermaid diagram (Breww renders
            // it in-chat) instead of a bulleted description or a downloadable file.
            ...(isConceptDiagram ? [{
              role: 'system' as const,
              content:
                'The user is asking for a DIAGRAM. You MUST render it as a Mermaid diagram inside a ```mermaid fenced code block ' +
                '(pick the right type: flowchart/graph TD for flows & processes, sequenceDiagram for interactions, erDiagram for ' +
                'entities, stateDiagram-v2 for states, mindmap for mind maps, classDiagram for classes), then a 2–3 sentence ' +
                'explanation. Do NOT answer with only a bulleted list of steps and do NOT create a downloadable file — output the ' +
                'actual ```mermaid block so it draws in the chat.',
            }] : []),
            ...history.map((m) => ({ role: m.role, content: m.content })),
            { role: 'user', content: trimmed },
          ],
          // A product pin (from a disambiguation click, or the product dropdown)
          // forces the turn to that connector. Absent on auto-route.
          ...(connectorHintForTurn ? { connectorHint: connectorHintForTurn } : {}),
          // Soft active-product hint (keeps follow-ups/confirmations on the product
          // that owns the in-progress flow). Not a hard pin — see above.
          ...(activeConnector ? { activeConnector } : {}),
          // "Answer here anyway" → lock intent so the switch offer doesn't loop.
          ...(forceWrapper ? { intentLock: true } : {}),
          // Token Saver opt-in → engine caps the agent loop + skips the MCP
          // composer LLM so the turn spends fewer tokens.
          ...(tokenSaver ? { tokenSaver: true } : {}),
          // Browser timezone → the server injects a real local clock into the
          // system prompt, so "what's the time?" answers in the USER's zone
          // rather than the server's. Best-effort; the server falls back to IST.
          ...(() => { try { return { timezone: Intl.DateTimeFormat().resolvedOptions().timeZone }; } catch { return {}; } })(),
          // Card-option click → owner-bound reply the server validates + pins.
          ...(cardReply ? { cardReply } : {}),
          // Recently generated files → the gateway attaches a text digest of each
          // so a follow-up about a generated document is grounded in its contents.
          ...(recentGeneratedFiles.length > 0 ? { generatedFiles: recentGeneratedFiles } : {}),
        }),
        signal: ctl.signal,
      });
      if (!res.ok || !res.body) {
        // Session expired mid-chat (401 invalid_token): the streaming path
        // bypasses the api-client wrapper, so redirect to /login here too
        // instead of rendering "invalid_token" in the chat error banner.
        if (res.status === 401) { handleSessionExpired(); return; }
        // The chat path returns JSON (not SSE) on error. Surface the engine's
        // own message — e.g. the token-limit notice with the actual numbers —
        // instead of a bare "HTTP 429".
        let msg = `HTTP ${res.status}`;
        try {
          const body = await res.json() as { detail?: string; code?: string; error?: { message?: string } };
          msg = body.detail || body.error?.message || msg;
        } catch { /* keep the status fallback */ }
        // Token allowance hit → refresh the usage state so the exhausted banner
        // + bar update immediately (don't wait for the next mount).
        if (res.status === 429) void refreshUsage();
        throw new Error(msg);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let wrapperSet = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split('\n\n');
        buffer = parts.pop() || '';
        for (const part of parts) {
          for (const line of part.split('\n')) {
            const t = line.trim();
            if (!t.startsWith('data:')) continue;
            const payload = t.slice(5).trim();
            if (!payload || payload === '[DONE]') continue;
            try {
              const obj = JSON.parse(payload) as {
                choices?: Array<{ delta?: { content?: string; reasoning?: string; reasoning_content?: string; tool_results?: unknown } }>;
                model?: string;
                object?: string;
                detail?: string;
                phase?: string;
                step?: string;
                note?: string;
                queries?: Array<{ q?: string; recency?: string }>;
                sources?: Array<{ title?: string; url?: string; domain?: string }>;
                count?: number;
                round?: number;
              };
              // Live progress ("Creating your image…", "Searching 3 queries",
              // "Assessing coverage"). The engine has emitted these all along;
              // this client dropped them on the floor and showed its generic
              // rotating "Thinking…" instead — so a turn spending 15s drawing a
              // picture looked exactly like a slow text reply.
              if (obj.object === 'brew.progress') {
                // Live one-line status — "Searching the web", "Reading sources",
                // etc. Drives the thinking indicator so a searching turn doesn't
                // look like a stalled text reply.
                const detail = typeof obj.detail === 'string' ? obj.detail : '';
                const phase = typeof obj.phase === 'string' ? obj.phase : '';
                if (detail) {
                  setMessages((m) => m.map((msg) => msg.id === assistantMsg.id
                    ? { ...msg, progress: detail, ...(phase ? { progressPhase: phase } : {}) }
                    : msg));
                }
                continue;   // not a content chunk
              }
              // Structured web_search SOURCES (Perplexity pattern) — the backend
              // sends the source array separately from the streamed Markdown so we
              // can render source cards + citation pills, not a prose "Sources:"
              // list the model typed.
              if (obj.object === 'brew.sources') {
                const srcs = (Array.isArray(obj.sources) ? obj.sources : []) as Array<{
                  n: number; title?: string; url?: string; domain?: string; publishedAt?: string;
                }>;
                if (srcs.length) {
                  setMessages((m) => m.map((msg) => msg.id === assistantMsg.id
                    ? { ...msg, sources: srcs } : msg));
                }
                continue;
              }
              // Structured news briefing (stories[] digest) — the frontend renders
              // the real briefing UI from this and hides the plain markdown answer.
              if (obj.object === 'brew.stories') {
                const so = obj as { digest?: { stories?: unknown[] }; confidence?: 'high' | 'medium' | 'low'; ts?: number };
                if (so.digest && Array.isArray(so.digest.stories) && so.digest.stories.length) {
                  // `ts` is when the SERVER synthesised the briefing, which is the
                  // honest freshness marker — not when this tab happens to render
                  // it. Falling back to now() only if the event omitted it.
                  const ts = typeof so.ts === 'number' ? so.ts : Date.now();
                  setMessages((m) => m.map((msg) => msg.id === assistantMsg.id
                    ? { ...msg, storiesDigest: so.digest as never, storiesTs: ts, ...(so.confidence ? { storiesConfidence: so.confidence } : {}) } : msg));
                }
                continue;
              }
              // Reasoning-model chain-of-thought — its own stream, never mixed
              // into the answer. Accumulate into the message's `thinking` so the
              // collapsible Thinking block renders it live.
              // Real chain-of-thought as a SEPARATE field. OpenAI-style models use
              // `reasoning`; DeepSeek / Groq-parsed use `reasoning_content`. Both are
              // the model's genuine thinking — capture either.
              const reasoningDelta = obj.choices?.[0]?.delta?.reasoning
                                  ?? obj.choices?.[0]?.delta?.reasoning_content;
              if (typeof reasoningDelta === 'string' && reasoningDelta) {
                if (thinkingStart == null) thinkingStart = Date.now();
                setMessages((m) => m.map((msg) => msg.id === assistantMsg.id
                  ? { ...msg, thinking: (msg.thinking || '') + reasoningDelta } : msg));
              }
              const rawDelta = obj.choices?.[0]?.delta?.content;
              if (typeof rawDelta === 'string' && rawDelta) {
                // Peel any inline <think>…</think> reasoning OUT of the answer and
                // INTO the Thinking panel — real reasoning, shown as reasoning.
                const { think, answer } = peelThink(rawDelta);
                if (think) {
                  if (thinkingStart == null) thinkingStart = Date.now();
                  setMessages((m) => m.map((msg) => msg.id === assistantMsg.id
                    ? { ...msg, thinking: (msg.thinking || '') + think } : msg));
                }
                if (answer) {
                  // First real ANSWER token → reasoning is done; stamp its duration
                  // so the block collapses to "Thought for Ns".
                  if (thinkingStart != null && !thinkingStamped) {
                    thinkingStamped = true;
                    const sec = Math.max(1, Math.round((Date.now() - thinkingStart) / 1000));
                    setMessages((m) => m.map((msg) => msg.id === assistantMsg.id ? { ...msg, thinkingSec: sec } : msg));
                  }
                  assistantText += answer;
                  setMessages((m) => m.map((msg) => msg.id === assistantMsg.id ? { ...msg, content: msg.content + answer } : msg));
                }
              }
              // Structured card sidecars — the engine emits these as ONE chunk
              // (delta.tool_results) for connectors that produce them (ESG). Attach
              // to the streaming assistant turn so Messages renders native cards.
              const trChunk = obj.choices?.[0]?.delta?.tool_results;
              if (Array.isArray(trChunk) && trChunk.length > 0) {
                const tr = trChunk as ToolResultSidecar[];
                assistantToolResults = tr;
                setMessages((m) => m.map((msg) => msg.id === assistantMsg.id ? { ...msg, toolResults: tr } : msg));
              }
              if (obj.model && !wrapperSet) {
                const w: string = obj.model;
                wrapperSet = true;
                finalWrapper = w;
                setMessages((m) => m.map((msg) => msg.id === assistantMsg.id ? { ...msg, wrapper: w } : msg));
              }
            } catch { /* tolerate */ }
          }
        }
      }
      // Persist when there's text OR cards — a card-only reply (empty narrative)
      // must still be saved with its sidecars so it rehydrates on reload.
      if (assistantText.trim() || (assistantToolResults && assistantToolResults.length > 0)) {
        await withRetry(() => api.post(`/api/chat-sessions/${sid}/append`, {
          role: 'assistant', content: assistantText, wrapper: finalWrapper,
          ...(assistantToolResults && assistantToolResults.length > 0
            ? { metadata: { toolResults: assistantToolResults } }
            : {}),
        }));
      }
      // Tag the session with the product this turn routed to, so it shows under
      // that product's dropdown (and switching scope keeps the chat). Only PATCH
      // when the product actually changed for this session.
      const routedScope = wrapperToScope(finalWrapper);
      if (routedScope && taggedScopeRef.current !== routedScope) {
        taggedScopeRef.current = routedScope;
        void api.patch(`/api/chat-sessions/${sid}`, { metadata: { product: routedScope } });
      }
      void refreshSessions();
      void refreshUsage();
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        const msg = (e as Error).message || 'Stream failed';
        setError(msg);
        // Persist the failure as an assistant turn in an ERROR state.
        //
        // The user turn is appended before the model call, so a failure used to
        // leave the question stored with no answer at all: on reload you saw
        // what you asked and no sign anything had gone wrong, and every later
        // turn was handed a transcript full of unanswered questions.
        //
        // Recorded EXPLICITLY rather than inferred, because failure cannot be
        // read from shape — two user messages in a row is perfectly normal and
        // looks identical to a failed turn, so any "drop the unpaired ones" rule
        // would silently delete real context. Deleting the user's message is the
        // other wrong answer: it makes the failure vanish, and the user is left
        // believing their message was never sent.
        //
        // `code` is the machine-readable part the context assembler filters on;
        // `content` is the human text, so the wording can change later without
        // rewriting stored history.
        void api.post(`/api/chat-sessions/${sid}/append`, {
          role: 'assistant',
          content: msg,
          metadata: { error: true, code: 'stream_failed' },
        }).catch(() => { /* best effort — never mask the original failure */ });
      }
    } finally {
      setStreaming(false);
      setStreamingId(null);
      abortRef.current = null;
    }
  }

  /** Stop the current turn.
   *
   *  A plain chat turn ends when we abort our own fetch — nothing else holds
   *  state. An AGENTIC turn does not: the Hermes bridge is a separate long-lived
   *  process that owns `busy` plus the per-turn response chain, and it only
   *  releases them on an explicit `interrupt` (which also DELETEs the upstream
   *  response server-side). Aborting the fetch alone left the bridge busy for
   *  good, so Stop looked like it did nothing and every later message came back
   *  "Still working on your previous message — please wait for it to finish or
   *  stop it first", with no way out but a new session.
   *
   *  Sent only while an agent turn is actually in flight: an interrupt on an
   *  idle bridge emits a stray turn_complete that would terminate the NEXT
   *  turn's stream. */
  function stop(): void {
    abortRef.current?.abort();
    if (!agentTurnRef.current) return;
    agentTurnRef.current = false;
    const token = getToken();
    void fetch('/api/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ type: 'interrupt', agent }),
    }).catch(() => { /* best effort — the turn is already over for the user */ });
  }

  // Barge-in context truncation (B1): when the user interrupts mid-reply, rewrite the last
  // assistant message to ONLY what was actually spoken aloud, so the history we send next turn
  // matches what they HEARD — the model won't reference or repeat the part it was cut off from.
  // Client-side only: the next send() ships this trimmed history; a reload still shows the
  // server's full copy (a server-side assistant-truncate endpoint is a later add).
  function truncateSpokenReply(spoken: string): void {
    const text = spoken.trim();
    if (!text) return;
    setMessages((ms) => {
      const lastIdx = ms.map((m) => m.role).lastIndexOf('assistant');
      if (lastIdx < 0) return ms;
      const cur = ms[lastIdx];
      if (!cur || !cur.content || text.length >= cur.content.length) return ms; // only trim, never extend
      return ms.map((m, i) => (i === lastIdx ? { ...m, content: text } : m));
    });
  }

  // Edit the LAST user prompt and re-run the turn (ChatGPT/Claude pattern). Only
  // the most recent prompt is editable — see Messages. We truncate that turn
  // (user + its assistant reply) on the server FIRST so a reload shows just the
  // edited exchange, then re-send against the history before it. On send failure
  // the truncate has already happened, which is the safe direction: a lost reply
  // beats a persisted duplicate, and the user can simply resend.
  async function editLastPrompt(newText: string): Promise<void> {
    const trimmed = newText.trim();
    if (!trimmed || streaming) return;
    const lastUserIdx = messages.map((m) => m.role).lastIndexOf('user');
    if (lastUserIdx < 0) return;
    const history = messages.slice(0, lastUserIdx);
    if (activeId) {
      try {
        await api.post(`/api/chat-sessions/${activeId}/truncate-last-user`, {});
      } catch {
        /* best-effort — proceed with the resend even if truncate failed */
      }
    }
    await send(trimmed, { historyOverride: history });
  }

  // Re-run the most recent turn with the SAME prompt. Identical machinery to
  // editLastPrompt (truncate that turn server-side, resend against the history
  // before it) — the only difference is the text is unchanged, so this is just
  // "give me another answer" rather than "I meant something else".
  async function regenerateLast(): Promise<void> {
    if (streaming) return;
    const lastUserIdx = messages.map((m) => m.role).lastIndexOf('user');
    if (lastUserIdx < 0) return;
    const lastPrompt = messages[lastUserIdx]?.content?.trim();
    if (!lastPrompt) return;
    await editLastPrompt(lastPrompt);
  }


  // Attach a document or image. Unlike the old one-shot vision flow, we upload
  // the raw bytes into the user's workspace (.uploads/<id>/) and add it as a
  // PENDING chip. The file isn't analyzed yet — the user types a prompt ("what
  // does this say?", "summarize it") and sends; the turn then routes to the
  // agent, which reads the file with its own tools. The file stays sticky for
  // the whole conversation so follow-ups need no re-upload.
  async function handleAttach(file: File): Promise<void> {
    setError(null);
    if (file.size > 25 * 1024 * 1024) {
      setError('That file is larger than 25 MB. Please attach a smaller file.');
      return;
    }
    // Make sure a session exists so the upload lands in this conversation.
    try { await ensureSession(); } catch (e) { setError((e as Error).message); return; }

    setUploading(true);
    try {
      const token = getToken();
      const res = await fetch('/api/chat/upload', {
        method: 'POST',
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          'Content-Type': file.type || 'application/octet-stream',
          'X-Filename': encodeURIComponent(file.name),
        },
        body: file,
      });
      // Keep session-expiry handling from staging, but on the NEW workspace
      // upload flow (the old vision-poll upload was replaced by /api/chat/upload).
      if (res.status === 401) { handleSessionExpired(); return; }
      if (!res.ok) {
        const detail = await res.json().catch(() => ({ error: `HTTP ${res.status}` }));
        throw new Error(detail.error || `Upload failed: HTTP ${res.status}`);
      }
      const meta = await res.json() as AttachedFile;
      setPendingAttachments((prev) =>
        prev.some((a) => a.fileId === meta.fileId) ? prev : [...prev, meta]);
    } catch (e) {
      setError(`Couldn't attach ${file.name}: ${(e as Error).message}`);
    } finally {
      setUploading(false);
    }
  }

  function removePendingAttachment(fileId: string): void {
    setPendingAttachments((prev) => prev.filter((a) => a.fileId !== fileId));
  }

  /**
   * One-click analysis of an attached spreadsheet/CSV.
   *
   * The server profiles the file (types, ranges, means, missing counts, top
   * values) and returns a ~1-2KB summary. We send THAT, never the file: a
   * 50k-row export would blow the context window, cost a fortune, and the model
   * still couldn't be trusted to total a column. The aggregates are computed in
   * code, so the figures it quotes are arithmetic rather than a guess.
   */
  async function quickAnalyze(file: AttachedFile): Promise<void> {
    if (streaming) return;
    try {
      const { prompt, model } = await api.post<{ prompt: string; model?: string }>(
        '/api/analyze/profile', { path: file.path });
      // The profile IS the data now, so drop the attachment: this turn is plain
      // chat. Leaving it attached routes the turn to the Hermes agent (which the
      // analysis doesn't need) and fails outright when the agent isn't running.
      setPendingAttachments((prev) => prev.filter((a) => a.fileId !== file.fileId));
      void send(
        `Analyse this dataset and give me the story in it — not a description of the columns.\n\n` +
        `${prompt}\n\n` +
        `Cover: (1) a 2-line bottom line, (2) the 3-4 findings that actually matter ` +
        `(concentrations, outliers, trends, anything at risk), (3) data-quality problems ` +
        `worth knowing about (missing values, suspicious ranges), (4) what I should look at next.\n` +
        `Include ONE chart as a \`\`\`mermaid block (xychart-beta or pie) for the single most ` +
        `useful comparison. Use only the figures given above — do not invent numbers, and say ` +
        `so plainly if the data can't support a conclusion.`,
        // Analysis is a reasoning task — pin it to the configured analysis model
        // (BREW_ANALYSIS_MODEL) instead of the fast everyday chat model.
        { plainChat: true, ...(model ? { forceWrapper: model } : {}) },
      );
    } catch (e) {
      setError(`Couldn't analyse ${file.name}: ${(e as Error).message}`);
    }
  }

  /**
   * Build an HTML dashboard from an attached spreadsheet and OPEN IT in the
   * right-side preview pane (Claude-Artifacts style) — no download.
   *
   * Rendered server-side from the profile (no model, no agent) to a self-contained
   * .html in the workspace, then shown inline in PreviewPane, which already offers
   * "Open in a new tab" and "Download". So the user explores it in place and only
   * saves if they want, instead of a file landing in Downloads they have to hunt
   * for and open. The data-viz rules live in dashboardPrompt(), so every dashboard
   * comes out consistent.
   */
  async function buildDashboard(file: AttachedFile): Promise<void> {
    if (streaming) return;
    try {
      const { path: outPath, name } = await api.post<{ path: string; name: string }>(
        '/api/analyze/dashboard', { path: file.path });
      setPreviewFile({ path: outPath, name });   // open in the split preview pane
    } catch (e) {
      setError(`Couldn't build a dashboard from ${file.name}: ${(e as Error).message}`);
    }
  }

  /**
   * Build a slide DECK from an attached spreadsheet and OPEN IT in the preview
   * pane — the same profile and computed marks as the dashboard, arranged as
   * slides to PRESENT rather than a page to explore. Server-rendered (no agent);
   * PreviewPane renders the deck inline (converting to PDF when needed) with
   * Open-in-new-tab + Download, so nothing is forced into the Downloads folder.
   */
  async function buildDeck(file: AttachedFile): Promise<void> {
    if (streaming) return;
    try {
      const { path: outPath, name } = await api.post<{ path: string; name: string }>(
        '/api/analyze/deck', { path: file.path });
      setPreviewFile({ path: outPath, name });   // open in the split preview pane
    } catch (e) {
      setError(`Couldn't build a deck from ${file.name}: ${(e as Error).message}`);
    }
  }

  // Double-click anywhere (outside text fields / clickable controls) toggles
  // the sidebar. Routes through the same setSidebarCollapsed as the rail
  // toggle, so the toggle button reflects the new state identically.
  function handleShellDoubleClick(e: React.MouseEvent<HTMLDivElement>): void {
    const el = e.target as HTMLElement;
    if (el.closest('input, textarea, select, button, a, [contenteditable="true"], .composer')) return;
    if (window.getSelection()?.toString()) return; // don't fight text selection
    // Desktop-only affordance — on mobile the sidebar is a drawer with no rail
    // state, so double-tap must not collapse it into the broken half-state.
    if (window.matchMedia('(max-width: 768px)').matches) return;
    setSidebarCollapsed((c) => !c);
  }

  const firstName = auth.user?.name?.split(' ')[0] || auth.user?.email?.split('@')[0] || 'there';
  // The EmptyState hero only renders on an empty chat (messages.length === 0), so
  // it should always greet the user. Keying this off the active session's title
  // made a brand-new / restored-but-empty session show its default "Untitled
  // chat" title here instead of the greeting.
  // Memoized per chat, not per render: pickGreeting() is random by design, so
  // calling it bare here would re-roll the line on every keystroke and every
  // stream chunk. activeId flips on New Chat / switching sessions — exactly the
  // moments a fresh greeting is wanted, and nowhere else.
  const greeting = useMemo(() => pickGreeting(firstName), [firstName, activeId]);

  // ── Token-usage banner state ──────────────────────────────────────────
  // Percent of the rolling 30-day allowance spent (0 when unlimited/unknown).
  const usagePct = usage && usage.limit ? Math.min(100, Math.round((usage.count / usage.limit) * 100)) : 0;
  // Allowance fully spent — the engine will 429 the next turn.
  const usageExhausted = !!usage && (usage.exceeded === true || (usage.limit != null && usage.count >= usage.limit));
  // Show the "turn on Token Saver" nudge once usage crosses 50% (and it isn't
  // already on, not exhausted, and the user hasn't dismissed it this session).
  const showSaverReminder = !!usage && usage.limit != null && !usageExhausted
    && !tokenSaver && usagePct >= 50 && !saverReminderDismissed;
  // The inline Token Saver pill in the composer only surfaces once usage crosses
  // 50% — below that there's nothing to save and it just adds clutter. (The
  // always-available toggle lives in Settings → Usage.)
  const showTokenSaverToggle = !!usage && usage.limit != null && usagePct >= 50;

  // Whether the ESG embedded UI is showing — in that state the native
  // composer is hidden entirely (the iframe owns its own input).
  const esgActive = productView === 'esg' && linkedConnectors.has('esg-caffe');

  // The native composer is built once and placed either centered inside the
  // hero (fresh chat) or docked at the bottom (active conversation).
  // The composer's model dropdown = Breww's core model tiers only (whitelist);
  // domain personas and solutions are excluded. Always keep the selected wrapper
  // present so its label resolves even if it isn't in the whitelist.
  const personaWrappers = wrappers.filter(
    // Model dropdown = core model tiers only. Keep a non-whitelisted *persona*
    // selection resolvable for its label, but NEVER a SOLUTION — a picked
    // solution belongs in the SolutionBar, not the model list (it used to leak
    // in via the bare `=== selectedWrapper` clause).
    (w) => MODEL_WRAPPER_NAMES.has(w.name) || (w.name === selectedWrapper && !isSolutionWrapper(w)),
  );

  // The chat's Solution selector only shows solutions the user has CONNECTED
  // (linkedConnectors = linked identity ∪ active subscription). Unconnected
  // solutions live in the sidebar "Solutions" section to browse/connect. So the
  // selector appears in-chat only once a solution is connected.
  const connectedSolutions: SolutionDef[] = wrappers
    .filter(isSolutionWrapper)
    .map((w) => ({ w, cid: solutionConnectorId(w) }))
    .filter(({ cid }) => linkedConnectors.has(cid))
    .map(({ w, cid }) => ({
      id: cid,
      wrapperName: w.name,
      displayName: w.displayName,
      description: w.description ?? null,
      icon: w.icon ?? null,
      authorized: true,
    }));
  const connectedSolutionNames = new Set(connectedSolutions.map((s) => s.wrapperName));
  const isGeneralScope = !connectedSolutionNames.has(selectedWrapper);

  const composerEl = (
    <>
      <SolutionBar
        solutions={connectedSolutions}
        activeWrapper={selectedWrapper}
        isGeneral={isGeneralScope}
        onSelectGeneral={() => setSelectedWrapper(DEFAULT_WRAPPER)}
        onSelectSolution={(s) => { if (s.wrapperName) setSelectedWrapper(s.wrapperName); }}
      />
      <Composer
      value={input}
      onChange={setInput}
      onSubmit={() => void send()}
      onStop={stop}
      streaming={streaming}
      wrappers={personaWrappers}
      // When a solution is active it owns the scope (shown in the SolutionBar);
      // the model dropdown reflects the underlying default tier, not the solution.
      selectedWrapper={isGeneralScope ? selectedWrapper : DEFAULT_WRAPPER}
      onSelectWrapper={setSelectedWrapper}
      products={[]}
      activeProductId={activeProductId}
      onPickConnector={(id) => chooseSurface(id)}
      onAttach={(file) => void handleAttach(file)}
      pendingAttachments={pendingAttachments}
      onRemoveAttachment={removePendingAttachment}
      onQuickAnalyze={(file) => void quickAnalyze(file)}
      onBuildDashboard={(file) => void buildDashboard(file)}
      onBuildDeck={(file) => void buildDeck(file)}
      uploading={uploading}
      tokenSaver={tokenSaver}
      onToggleTokenSaver={() => setTokenSaver((v) => !v)}
      showTokenSaver={showTokenSaverToggle}
      speakReplies={speakReplies}
      onToggleSpeakReplies={() => setSpeakReplies((v) => !v)}
      voiceModeOpen={voiceModeOpen}
      onOpenVoiceMode={() => setVoiceModeOpen(true)}
      engineState={engineState[agent]}
      />
    </>
  );

  // In-chat token nudge: the 50% "turn on Token Saver" reminder, docked just
  // above the composer. The exhausted case is handled by the bottom .chat-error
  // (the engine's own message), so there's no separate exhausted banner.
  const usageBanner = showSaverReminder ? (
    <div className="token-banner token-banner--warn token-banner--float" role="status">
      <span className="token-banner__text">
        <span className="token-banner__line">You've used {usagePct}% of your monthly {usage?.creditsShown ? 'credits' : 'tokens'}.</span>
        <span className="token-banner__line">Turn on <strong>Token Saver</strong> to make Breww stretch what's left.</span>
      </span>
      <button
        type="button"
        className="token-banner__cta"
        aria-label="Turn on Token Saver"
        onClick={() => {
          setTokenSaver(true);
        }}
      >
        Turn on
      </button>
      <button
        type="button"
        className="token-banner__dismiss"
        aria-label="Dismiss"
        onClick={() => setSaverReminderDismissed(true)}
      >
        ✕
      </button>
    </div>
  ) : null;

  return (
    <div
      className="brew-shell"
      data-route="chat"
      data-sidebar={sidebarCollapsed ? 'collapsed' : 'open'}
      data-drawer={drawerOpen ? 'open' : 'closed'}
      onDoubleClick={handleShellDoubleClick}
    >
      <OnboardingBanner />
      {/* Mobile-only: dims the chat while the drawer is open; tap to dismiss.
          Hidden above the drawer breakpoint via CSS. */}
      <button
        type="button"
        className="brew-drawer-backdrop"
        aria-label="Close menu"
        hidden={!drawerOpen}
        onClick={closeDrawer}
      />
      <SidebarNav
        collapsed={sidebarCollapsed}
        onNewChat={() => { handleNewChat(); closeDrawer(); }}
        onToggleSidebar={() => {
          // On mobile the sidebar is an overlay drawer, not a collapsible rail —
          // so its toggle button closes the drawer instead of collapsing to the
          // (meaningless here) icon rail, which otherwise left a broken half-state.
          if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
            closeDrawer();
          } else {
            setSidebarCollapsed((c) => !c);
          }
        }}
        userName={auth.user?.name ?? null}
        userEmail={auth.user?.email ?? null}
        userRole={auth.user?.role ?? null}
        onOpenSettings={openSettings}
        onLogout={async () => {
          await auth.logout();
          navigate('/login', { replace: true });
        }}
        renderSection={(s, hovered) => {
          if (s === 'workflows') return <WorkflowsPanel hovered={hovered} onRun={(text) => setInput(text)} />;
          if (s === 'extensions') return <ExtensionsPanel hovered={hovered} />;
          if (s === 'files') return <FilesPanel expanded={hovered} refreshKey={filesRefreshKey} onOpen={(path, name) => setPreviewFile({ path, name })} />;
          return <ConnectorsPanel hovered={hovered} activeConnectorId={activeProductId} />;
        }}
        renderRecent={() => (
          <ChatSidebarPanel
            sessions={sessions}
            activeId={activeId}
            loading={sidebarLoading}
            hasMore={sessionsHasMore}
            onLoadMore={() => void loadMoreSessions()}
            loadingMore={sessionsLoadingMore}
            onRefresh={() => void refreshSessions()}
            onSelect={(id) => { handleSelectSession(id); closeDrawer(); }}
            onDelete={(id) => void deleteSession(id)}
          />
        )}
      />

      <main
        className="main"
        id="app"
        data-solution={themeScope}
      >
        {/* Mobile-only: opens the sidebar drawer. Hidden on desktop via CSS. */}
        <button
          type="button"
          className="brew-drawer-toggle"
          aria-label="Open menu"
          aria-expanded={drawerOpen}
          onClick={openDrawer}
        >
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M3 6h18M3 12h18M3 18h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
        </button>
        {themeScope === 'lumara' && (
          <>
            {(['left', 'right'] as const).map((side) => (
              <div key={side} className={`lumara-blind lumara-blind--${side}`} aria-hidden="true">
                <span className="lumara-blind__frame" />
                <span className="lumara-blind__headrail" />
                <span className="lumara-blind__slats" />
                <span className="lumara-blind__bottomrail" />
                {side === 'right' && (
                  <>
                    <span className="lumara-blind__cord" />
                    <span className="lumara-blind__hand">✊</span>
                  </>
                )}
              </div>
            ))}
          </>
        )}
        {usageBanner}
        {esgActive ? (
          /* The ESG team's brew-mfe — same backend, ESG-branded UI, includes ESG
             Connect login/OTP. CORS-relaxed in dev so this iframe just works.
             Double-gated: only renders when the productView toggle is on AND the
             user actually has an active ESG Caffe identity. */
          <section className="chat" id="chat" style={{ padding: 0 }}>
            {esgIframeStatus !== 'ready' && (
              <div className="chat__surface-loading" aria-live="polite">
                <div className="chat__surface-card">
                  <strong>Opening ESG Caffe</strong>
                  <span>Connecting to the embedded ESG workspace…</span>
                </div>
              </div>
            )}
            <iframe
              ref={esgIframeRef}
              src={esgIframeUrl}
              title="ESG Breww chat"
              onError={() => {
                fallbackToNativeEsgChat(
                  `ESG embedded UI couldn't be reached at ${ESG_MFE_BASE_URL}. Switched to native Breww chat.`,
                );
              }}
              style={{
                flex: 1,
                width: '100%',
                height: '100%',
                border: 0,
                background: 'var(--bg, #0f1115)',
                visibility: esgIframeStatus === 'ready' ? 'visible' : 'hidden',
              }}
              allow="clipboard-read; clipboard-write; microphone"
            />
          </section>
        ) : (
          <section className="chat" id="chat" aria-live="polite" ref={scrollerRef} onScroll={onChatScroll} style={{ overflowAnchor: 'none' }}>
            {msgLoading ? (
              <div className="msg-skel-list" aria-hidden="true" data-testid="msg-skeleton">
                <div className="msg-skel msg-skel--user">
                  <div className="msg-skel__line" style={{ width: '60%' }} />
                  <div className="msg-skel__line" style={{ width: '40%' }} />
                </div>
                <div className="msg-skel">
                  <div className="msg-skel__line" style={{ width: '88%' }} />
                  <div className="msg-skel__line" style={{ width: '94%' }} />
                  <div className="msg-skel__line" style={{ width: '72%' }} />
                </div>
                <div className="msg-skel msg-skel--user">
                  <div className="msg-skel__line" style={{ width: '50%' }} />
                </div>
                <div className="msg-skel">
                  <div className="msg-skel__line" style={{ width: '80%' }} />
                  <div className="msg-skel__line" style={{ width: '90%' }} />
                </div>
              </div>
            ) : messages.length === 0 ? (
              <EmptyState
                greeting={greeting}
                composer={composerEl}
                suggestions={STARTER_SUGGESTIONS}
                onPickSuggestion={(prompt) => {
                  setInput(prompt);
                  // Focus the composer and drop the cursor at the end so the user
                  // finishes the lead-in and hits Enter.
                  requestAnimationFrame(() => {
                    const el = document.getElementById('input') as HTMLTextAreaElement | null;
                    if (el) { el.focus(); const n = prompt.length; el.setSelectionRange(n, n); }
                  });
                }}
              />
            ) : (
              <>
              {msgLoadingOlder && (
                <div style={{ textAlign: 'center', padding: '8px 0', fontSize: '0.8em', opacity: 0.6 }}>Loading earlier messages…</div>
              )}
              <Messages
                messages={messages}
                streamingAssistantId={streamingId}
                onEditLast={(t) => void editLastPrompt(t)}
                onRegenerate={() => void regenerateLast()}
                onOpenFile={(path, name) => setPreviewFile({ path, name })}
                onSelect={(t) => void send(t)}
                onCardReply={(value, card) => void send(value, { cardReply: { ...card, value } })}
                onPickProduct={(originalPrompt, value) => {
                  // "Answer here anyway" → __stay__:<wrapper>; otherwise it's a connectorId to switch to.
                  if (value.startsWith('__stay__:')) {
                    void send(originalPrompt, { forceWrapper: value.slice('__stay__:'.length) });
                  } else {
                    // Switch the WHOLE chat to that product so future turns route
                    // there too (not just this one) — drop the manual wrapper pin
                    // and move the scope to the product. Then route this turn.
                    setSelectedWrapper(DEFAULT_WRAPPER);
                    const scope = CONNECTOR_TO_SCOPE[value];
                    if (scope) setChatScope(scope);
                    void send(originalPrompt, { connectorHint: value });
                  }
                }}
              />
              </>
            )}
            {error && <div className="chat-error">{error}</div>}
          </section>
        )}

        {/* Return-to-live control. Appears only once the reader has genuinely
            scrolled away, so it never sits on screen during normal reading. While
            a reply is streaming it also says so — that is the case where content
            is arriving somewhere the reader cannot see. */}
        {!esgActive && messages.length > 0 && awayFromBottom ? (
          <button
            type="button"
            className={`jump-latest${streaming ? ' is-live' : ''}`}
            onClick={jumpToLatest}
            aria-label={streaming ? 'Still writing — jump to latest' : 'Jump to latest'}
            title={streaming ? 'Still writing — jump to latest' : 'Jump to latest'}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M12 5v14m0 0-6-6m6 6 6-6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        ) : null}

        {/* Active conversation: the composer docks at the bottom. On a fresh
            chat it lives centered inside the hero (see EmptyState above). The
            ESG embedded UI state hides it entirely. */}
        {!esgActive && messages.length > 0 && composerEl}
      </main>

      {/* Preview pane — flex sibling of <main>, so opening it shrinks the chat
          instead of overlaying it (Claude Artifacts split view). */}
      {previewFile && <PreviewPane target={previewFile} onClose={() => setPreviewFile(null)} />}

      {/* Agent Studio — the Mission OS workspace (Universal Agent Blueprint §5/§8).
          Fills the right canvas with the latest turn's mission: goal, agents, real
          deliverables and executable actions. Only for turns that are actually a
          mission (has agents / deliverables / actions). Yields the gutter to the
          preview pane (only one shows), and hidden in the ESG embedded UI. */}
      {!previewFile && !esgActive && (() => {
        // The studio is PINNED to a specific mission (activeMissionId), set when
        // /api/mission returns a showable plan. Pinning keeps it stable while a
        // deliverable build turn streams (that turn has no mission of its own), and
        // means dismissing hides the current mission without reviving an older one.
        const missionMsg = activeMissionId
          ? messages.find((m) => m.id === activeMissionId && m.role === 'assistant') ?? null
          : null;
        const plan = missionMsg?.missionPlan ?? null;
        // Same predicate the effect pinned with — one source of truth, no drift.
        const hasMission = missionShowable(plan, missionMsg?.missionCommitted);
        // While the plan LLM is still running (and no real studio is up yet), show the
        // skeleton so the panel is present from the first moment — no cold late pop.
        if (!hasMission) {
          if (!planningMissionId) return null;
          return (
            <>
              <button type="button" className="mstudio-fab" data-busy="true" data-hidden={studioMobileOpen ? "true" : "false"} data-desktop-show={desktopStudioHidden ? "true" : "false"} onClick={openStudio} aria-label="Open Agent Studio">
                <span className="mstudio-fab__icon" aria-hidden="true">✦</span>
                <span className="mstudio-fab__label">Forming mission…</span>
              </button>
              <div className="mstudio-backdrop" data-open={studioMobileOpen ? 'true' : 'false'} aria-hidden="true" onClick={() => setStudioMobileOpen(false)} />
              <MissionStudioSkeleton
                mobileOpen={studioMobileOpen}
                desktopHidden={desktopStudioHidden}
                onCollapseMobile={() => setStudioMobileOpen(false)}
                onDismiss={closeStudio}
              />
            </>
          );
        }
        // The just-finished turn's real outcome — makes "Built ✓" honest (only a turn
        // that actually produced a file / didn't error counts as done).
        const lastMsg = messages[messages.length - 1];
        const lastResult = {
          id: lastMsg?.id ?? '',
          files: lastMsg?.role === 'assistant' && Array.isArray(lastMsg.files) ? lastMsg.files.length : 0,
          error: !!(lastMsg?.role === 'assistant' && lastMsg.error),
        };
        const chipCount = plan!.deliverables.length + plan!.actions.length;
        return (
          <>
            {/* Trigger pill — opens the sheet on mobile, reopens the hidden panel on
                desktop. Always available so the studio can never get "stuck closed". */}
            <button type="button" className="mstudio-fab" data-hidden={studioMobileOpen ? "true" : "false"} data-desktop-show={desktopStudioHidden ? "true" : "false"} onClick={openStudio} aria-label="Open Agent Studio">
              <span className="mstudio-fab__icon" aria-hidden="true">🎯</span>
              <span className="mstudio-fab__label">Agent Studio</span>
              {chipCount > 0 && <span className="mstudio-fab__badge">{chipCount}</span>}
            </button>
            <div className="mstudio-backdrop" data-open={studioMobileOpen ? 'true' : 'false'} aria-hidden="true" onClick={() => setStudioMobileOpen(false)} />
          <MissionStudio
            plan={plan!}
            missionKey={missionMsg!.id}
            missionQuery={missionMsg!.missionQuery || plan!.goal}
            busy={streaming}
            lastResult={lastResult}
            mobileOpen={studioMobileOpen}
            desktopHidden={desktopStudioHidden}
            onCollapseMobile={() => setStudioMobileOpen(false)}
            onRun={(prompt, route) => { setStudioMobileOpen(false); void send(prompt, route === 'build' ? { missionRoute: 'build' } : undefined); }}
            onRunAll={(dels) => {
              // One tap → build every deliverable. The agentic builder produces
              // multiple files in a single turn, so we fold them into one request
              // and force the build route so it never lands as a chat paragraph.
              // Close the mobile sheet so the user sees the build happen in chat.
              setStudioMobileOpen(false);
              const list = dels.map((d, i) => `${i + 1}. ${d.label}${d.format ? ` (${d.format.toUpperCase()})` : ''} — ${d.prompt}`).join('\n');
              void send(`Build all ${dels.length} deliverables for this mission, each as its own file:\n${list}`, { missionRoute: 'build' });
            }}
            onUpgrade={() => openSettings('billing')}
            onGather={(query, exclude) =>
              api.post<{ sources?: MissionSource[]; images?: MissionImage[]; live?: { provider: string; count: number; attempted?: boolean } }>('/api/mission/gather', { query, exclude })
                .then((r) => ({ sources: r?.sources ?? [], images: r?.images ?? [], ...(r?.live ? { live: r.live } : {}) }))
                .catch(() => ({ sources: [], images: [], live: { provider: 'error', count: 0, attempted: true } }))}
            onDismiss={closeStudio}
          />
          </>
        );
      })()}

      <SettingsModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        initialTab={settingsTab}
        tokenSaver={tokenSaver}
        onToggleTokenSaver={() => setTokenSaver((v) => !v)}
      />

      <VoiceMode
        open={voiceModeOpen}
        streaming={streaming}
        lastAssistant={lastAssistant}
        onSend={(t) => void send(t, { voiceReply: true })}
        onStop={stop}
        onTruncate={truncateSpokenReply}
        onClose={() => setVoiceModeOpen(false)}
      />
    </div>
  );
}
